// Pure renderer shared by the print page and the archive worker (Deno).
// - No DOM, no React, no clock, no network: the same bundle and options always give the
//   same string (the archive stores its SHA-256).
// - Imports only types, the shared catalogue and the pure OM-approval helpers in src/domain,
//   with explicit .ts extensions so Deno can load the same file the app bundles.
// - Every value taken from the record goes through the html`` template below, which
//   HTML-escapes it. Only this file's own markup and CSS are emitted unescaped.
// - Unsigned steps render as visibly different "NOT SIGNED" boxes; nothing here can make
//   an unsigned step look signed (blueprint: unsigned placeholders must not look like
//   completed attestations).
// - Work eligibility is never printed: it is a live server decision that a paper copy
//   cannot keep current. The copy points to the live permit instead.
// Contract: renderPermitHtml(bundle, options) => complete HTML document string.
import {
  AA_CLOSEOUT_CHECKS,
  AA_VALIDATION_CHECKS,
  ACCEPTANCE_DECLARATIONS,
  CANCELLATION_REASONS,
  CLASS_ACTIVITIES,
  CYCLE_STATES,
  EVENT_KIND_LABELS,
  GAS_SPECIES,
  GAS_TEST_KINDS,
  GAS_TRIGGERS,
  ISOLATION_DISPOSITION_LABELS,
  ISOLATION_KINDS,
  MEETING_CHECKS,
  PERMIT_CLASSES,
  PERMIT_SECTIONS,
  PH_CANCELLATION_DECLARATIONS,
  PREISSUE_CHECKS,
  PRESSURE_TEST_GROUP,
  ROLES,
  SUSPENSION_REASONS,
  SYNTHETIC_NOTICE,
  TBT_TOPICS,
} from '../../domain/catalogue.ts'
import type { Option } from '../../domain/catalogue.ts'
import { EXTENSION_STATUS_TEXT, extensionSummary } from '../../domain/extension.ts'
import { currentOmApproval, omApprovalCarriedOver, omApprovalStatus } from '../../domain/omApproval.ts'
import type {
  ArchiveBundle,
  AttestationView,
  CredentialRef,
  CycleView,
  GasTestView,
  PermitBundle,
  PersonRef,
  RevisionView,
  SuspensionView,
  ToolboxTalkView,
} from '../../domain/types.ts'
import { STAGING_NOTICE } from '../../domain/identity.ts'
import { BADREOR_LOGO_DATA_URI, BADREOR_LOGO_HEIGHT, BADREOR_LOGO_WIDTH } from './logo.ts'

// permit-html-3 (0016): the RS signs every step the Area Authority signed (historic AA
// signatures read "Area Authority (historic)"), and section 8 shows the Operations Manager approval.
// permit-html-4 (0020): section 8 "Authorisation and extensions" shows each extension request
// with its decision and signatures; an extension carries the OM approval over; the header shows
// the original end when the permit was extended.
// permit-html-5 (0022): a staging record (permit number STG-, or rendered in a staging
// environment) carries a STAGING watermark, header mark and note; other output is unchanged
// apart from this version. The bundle JSON is unchanged.
export const RENDER_TEMPLATE_VERSION = 'permit-html-5'

export type RenderOptions = {
  mode: 'print' | 'archive'
  /** SVG markup of a QR code that locates the live permit. Embedded as an image, never as markup. */
  qrSvg?: string
  /** The address the QR code encodes; printed as text. */
  locatorUrl?: string
  /** ISO time this document is generated for. The renderer never reads the clock. */
  generatedAt?: string
  /** The database environment the copy is printed from (ptw_me), when known. */
  environmentKind?: string
}

export const SYNTHETIC_WATERMARK = 'SYNTHETIC DEMONSTRATION - NOT A PERMIT TO WORK'
export const STAGING_WATERMARK = 'STAGING TEST RECORD - NOT A PERMIT TO WORK'

/**
 * A staging (verification) record: numbered STG- by the database (0022), or printed from a
 * staging environment. Its people may be real, but the record is a test, never a permit.
 */
export function isStagingRecord(bundle: { permit?: { permit_number?: string | null } | null } | null | undefined, environmentKind?: string | null): boolean {
  return environmentKind === 'staging' || /^STG-/.test(bundle?.permit?.permit_number ?? '')
}
const UNAPPROVED_WATERMARK = 'RULES NOT APPROVED - NOT A PERMIT TO WORK'
const NOT_AUTHORISED_WATERMARK = 'NOT AUTHORISED - NOT A PERMIT TO WORK'
const QR_NOTE = 'QR is a locator, not an authorisation'

type AnyBundle = ArchiveBundle | PermitBundle
type Rec = Record<string, unknown>

// -----------------------------------------------------------------------------
// Safe HTML
// -----------------------------------------------------------------------------
const ENTITY: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

/** Escapes any value for HTML text or a double-quoted attribute value. */
export function escapeHtml(value: unknown): string {
  return toPlain(value).replace(/[&<>"']/g, (c) => ENTITY[c] ?? c)
}

function toPlain(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  if (typeof value === 'boolean' || typeof value === 'bigint') return String(value)
  return stableJson(value)
}

/** JSON with sorted keys, so free-form details render the same way every time. */
function stableJson(value: unknown, depth = 0): string {
  if (depth > 12) return '"..."'
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'string') return JSON.stringify(value)
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null'
  if (typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return `[${value.map((v) => stableJson(v, depth + 1)).join(', ')}]`
  if (typeof value === 'object') {
    const o = value as Rec
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}: ${stableJson(o[k], depth + 1)}`)
      .join(', ')}}`
  }
  return JSON.stringify(String(value))
}

/** Markup produced by this file. Anything else interpolated into html`` is escaped. */
class Html {
  readonly value: string
  constructor(value: string) {
    this.value = value
  }
}

const EMPTY = new Html('')

function html(strings: TemplateStringsArray, ...values: unknown[]): Html {
  let out = strings[0]
  for (let i = 0; i < values.length; i += 1) out += fragment(values[i]) + strings[i + 1]
  return new Html(out)
}

function fragment(value: unknown): string {
  if (value instanceof Html) return value.value
  if (Array.isArray(value)) return value.map(fragment).join('')
  if (value === null || value === undefined || value === false) return ''
  return escapeHtml(value)
}

function joinHtml(parts: readonly Html[], separator: Html = EMPTY): Html {
  return new Html(parts.map((p) => p.value).join(separator.value))
}

// -----------------------------------------------------------------------------
// Small value helpers (defensive: an archive bundle omits several live keys)
// -----------------------------------------------------------------------------
function arr<T>(value: readonly T[] | null | undefined): readonly T[] {
  return Array.isArray(value) ? value : []
}

function asRec(value: unknown): Rec {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Rec) : {}
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : toPlain(value)
}

function sorted<T>(items: readonly T[], cmp: (a: T, b: T) => number): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((p, q) => cmp(p.item, q.item) || p.index - q.index)
    .map((p) => p.item)
}

function cmpNum(a: unknown, b: unknown): number {
  const x = typeof a === 'number' ? a : Number.POSITIVE_INFINITY
  const y = typeof b === 'number' ? b : Number.POSITIVE_INFINITY
  return x === y ? 0 : x < y ? -1 : 1
}

function cmpText(a: unknown, b: unknown): number {
  const x = str(a)
  const y = str(b)
  return x === y ? 0 : x < y ? -1 : 1
}

function timeValue(iso: unknown): number {
  return typeof iso === 'string' ? Date.parse(iso) : Number.NaN
}

/** Compares ISO times by instant (offsets differ between sources), then as text. */
function cmpTime(a: unknown, b: unknown): number {
  const x = timeValue(a)
  const y = timeValue(b)
  if (!Number.isNaN(x) && !Number.isNaN(y) && x !== y) return x < y ? -1 : 1
  if (Number.isNaN(x) !== Number.isNaN(y)) return Number.isNaN(x) ? 1 : -1
  return cmpText(a, b)
}

const ACRONYMS: Record<string, string> = {
  aa: 'AA', agt: 'AGT', cse: 'CSE', dra: 'DRA', gis: 'GIS', h2s: 'H2S', hse: 'HSE', jhsep: 'JHSEP', lel: 'LEL',
  moc: 'MoC', mopo: 'MOPO', oac: 'OAC', om: 'OM', pa: 'PA', pc: 'PC', ph: 'PH', ptw: 'PTW', rs: 'RS', simops: 'SIMOPS',
  tbt: 'TBT', tric: 'TRIC', id: 'id',
}

/** 'application_and_jhsep_complete' -> 'Application and JHSEP complete'. */
function humanize(code: unknown): string {
  if (typeof code !== 'string' || code.trim() === '') return '-'
  const words = code
    .split(/[_\s]+/)
    .filter((w) => w !== '')
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.toLowerCase())
  const text = words.join(' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function optionLabel(options: readonly Option[], code: unknown): string {
  if (typeof code !== 'string' || code === '') return '-'
  const found = options.find((o) => o.code === code)
  return found ? found.label : `${humanize(code)} (${code})`
}

/** Label plus the code, unless the label already shows it ('Flammable (LEL)'). */
function labelWithCode(options: readonly Option[], code: unknown): string {
  const label = optionLabel(options, code)
  const c = str(code)
  return c === '' || label.indexOf(c) >= 0 ? label : `${label} (${c})`
}

const EXAM_KINDS: Record<string, string> = {
  pa_rs_pre_authorisation: 'PA/RS visit before authorisation',
  // Exam kinds keep their historic aa_ codes; the RS examines from 0016.
  aa_first_day: 'First-day worksite examination at validation',
  aa_daily: 'Daily worksite examination at validation',
  aa_revalidation: 'Worksite examination at revalidation',
}

function examLabel(code: unknown): string {
  return typeof code === 'string' && EXAM_KINDS[code] ? EXAM_KINDS[code] : humanize(code)
}

const ROLE_TABLE = ROLES as unknown as Record<string, { label: string; short: string } | undefined>

function roleLabel(code: unknown): string {
  if (typeof code !== 'string' || code === '') return '-'
  const role = ROLE_TABLE[code]
  return role ? `${role.label} (${role.short})` : code
}

function classLabel(code: unknown): string {
  return typeof code === 'string' && code !== '' ? optionLabel(PERMIT_CLASSES, code) : '-'
}

const CAPACITY_TEXT: Record<string, string> = {
  application_and_jhsep_complete: 'Application and Job HSE plan complete',
  meeting_review: 'Permit meeting review',
  return_for_revision: 'Returned for revision',
  supporting_record_entered: 'Supporting record entered (not a verification)',
  authorisation: 'Permit authorised',
  validation: 'Daily validation',
  tbt_understanding: 'Understood the toolbox talk and TRIC',
  system_gas_result: 'Stop raised by an unsafe gas result',
  fact_reporter: 'Reported a fact (no licence needed)',
  oac_agreement: 'Other affected custodian agreement',
  pc_approval: 'Production Coordinator approval',
  worksite_examination: 'Worksite examination',
  supporting_record_verification: 'Supporting record verified',
  supporting_record_withdrawal: 'Supporting record withdrawn',
  preissue: 'Daily pre-issue',
  gas_test: 'Signed gas test',
  toolbox_talk_given: 'Toolbox talk given',
  acceptance: 'Permit Holder acceptance',
  suspension_report: 'Formal stop report',
  suspension_declaration: 'Permit Holder suspension declaration (site left safe)',
  suspension_confirmation: 'Suspension confirmed by the supervisor',
  ph_cancellation: 'Cancelled by the Permit Holder',
  closeout: 'Closeout after the worksite inspection',
  om_approval: 'Operations Manager approval',
  permit_om_approval: 'Operations Manager approval',
  extension_request: 'Extension requested',
  extension_approval: 'Extension approved',
  extension_refusal: 'Extension refused',
  extension_withdrawal: 'Extension request withdrawn',
}

function capacityLabel(code: unknown): string {
  if (typeof code !== 'string' || code === '') return '-'
  return CAPACITY_TEXT[code] ?? humanize(code)
}

const POLICY_STATUS_TEXT: Record<string, string> = {
  synthetic_fixture: 'Synthetic fixture - not approved policy',
  candidate: 'Candidate - not approved policy',
  approved: 'Approved',
  withdrawn: 'Withdrawn - not current policy',
}

function policyStatusLabel(status: unknown): string {
  if (typeof status !== 'string' || status === '') return 'Status not stated - not approved policy'
  return POLICY_STATUS_TEXT[status] ?? `${humanize(status)} - approval not confirmed`
}

function cycleStateLabel(state: unknown): string {
  if (typeof state !== 'string' || state === '') return '-'
  return CYCLE_STATES[state]?.label ?? humanize(state)
}

// -----------------------------------------------------------------------------
// Time: Asia/Muscat is UTC+4 all year (no daylight saving), so local time is computed
// without Intl and is identical in every runtime.
// -----------------------------------------------------------------------------
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MUSCAT_OFFSET_MS = 4 * 60 * 60 * 1000

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function muscat(iso: unknown, withSeconds = false): string {
  if (typeof iso !== 'string' || iso.trim() === '') return '-'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  const d = new Date(ms + MUSCAT_OFFSET_MS)
  const time = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}${withSeconds ? `:${pad2(d.getUTCSeconds())}` : ''}`
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ${time} GST`
}

function windowText(from: unknown, to: unknown): string {
  if (isBlank(from) && isBlank(to)) return '-'
  return `${muscat(from)} to ${muscat(to)}`
}

/** 'YYYY-MM-DD' (a calendar date, no time zone) -> '24 Apr 2026'. */
function dateOnly(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '-'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return value
  const month = Number(m[2])
  return month >= 1 && month <= 12 ? `${Number(m[3])} ${MONTHS[month - 1]} ${m[1]}` : value
}

// -----------------------------------------------------------------------------
// Building blocks
// -----------------------------------------------------------------------------
type Cell = Html | string | number | boolean | null | undefined
type Row = readonly [string, Cell]

function yesNo(value: unknown, missing = 'Not answered'): Html {
  if (value === true) return html`<span class="yes">Yes</span>`
  if (value === false) return html`<span class="no">No</span>`
  return html`<span class="missing">${missing}</span>`
}

function cell(value: Cell): Html {
  if (value instanceof Html) return html`<td>${value}</td>`
  if (typeof value === 'boolean') return html`<td>${yesNo(value)}</td>`
  if (isBlank(value)) return html`<td><span class="muted">-</span></td>`
  return html`<td class="txt">${value}</td>`
}

function kv(rows: readonly Row[]): Html {
  if (rows.length === 0) return EMPTY
  return html`<table class="kv"><tbody>${rows.map(([k, v]) => html`<tr><th scope="row">${k}</th>${cell(v)}</tr>`)}</tbody></table>`
}

function grid(headers: readonly string[], rows: ReadonlyArray<readonly Cell[]>, extraClass = ''): Html {
  if (rows.length === 0) return EMPTY
  return html`<div class="tw"><table class="grid ${extraClass}"><thead><tr>${headers.map((h) => html`<th scope="col">${h}</th>`)}</tr></thead><tbody>${rows.map((r) => html`<tr>${r.map(cell)}</tr>`)}</tbody></table></div>`
}

function note(text: unknown): Html {
  return html`<p class="note">${text}</p>`
}

function mono(value: unknown): Html {
  return isBlank(value) ? html`<span class="muted">-</span>` : html`<span class="mono">${value}</span>`
}

function tag(text: string, tone: 'ok' | 'warn' | 'bad' | 'info' | 'syn' | 'neutral'): Html {
  return html`<span class="tag tag-${tone}">${text}</span>`
}

function h3(text: unknown, aside: Html = EMPTY): Html {
  return html`<h3 class="sub">${text}${aside}</h3>`
}

function h4(text: unknown, aside: Html = EMPTY): Html {
  return html`<h4 class="sub2">${text}${aside}</h4>`
}

function personText(person: PersonRef | null | undefined, organisation?: string | null): string {
  if (!person) return '-'
  const org = organisation ?? person.organisation
  return `${person.display_name || 'Unnamed person'}${org ? ` (${org})` : ''}`
}

function checklist(options: readonly Option[], values: unknown, missing = 'Not recorded'): Html {
  const record = asRec(values)
  const known = new Set(options.map((o) => o.code))
  const rows: Cell[][] = options.map((o, i) => [String(i + 1), o.label, yesNo(record[o.code], missing)])
  Object.keys(record)
    .filter((k) => !known.has(k))
    .sort()
    .forEach((k) => rows.push(['+', `${humanize(k)} (${k}) - not in the catalogue`, yesNo(record[k], missing)]))
  return grid(['#', 'Item', 'Answer'], rows, 'checks')
}

/** Free-form record (e.g. event details): sorted keys, escaped values, known ids named. */
function details(value: unknown, nameOf: (v: unknown) => string | null = () => null): Html {
  const record = asRec(value)
  const keys = Object.keys(record).sort()
  if (keys.length === 0) return html`<span class="muted">-</span>`
  return joinHtml(
    keys.map((k) => {
      const v = record[k]
      const shown = typeof v === 'string' ? v : stableJson(v)
      const name = nameOf(v)
      return html`<span class="det"><span class="mono">${k}</span>: ${shown}${name ? html` <span class="idname">(${name})</span>` : EMPTY}</span>`
    }),
    html`<br>`,
  )
}

// -----------------------------------------------------------------------------
// Signatures
// -----------------------------------------------------------------------------
type Expect = { role?: string; what: string }

function credentialText(credential: CredentialRef | null | undefined): Html {
  if (!credential) return html`<span class="muted">None recorded</span>`
  const c = credential as CredentialRef & Rec
  const parts: string[] = []
  parts.push(`${c.role_code || '?'} ${humanize(c.credential_kind)}`)
  if (!isBlank(c.acting_role) && c.acting_role !== c.role_code) parts.push(`acting as ${str(c.acting_role)}`)
  if (!isBlank(c.issuer)) parts.push(`issuer: ${str(c.issuer)}`)
  parts.push(`issued ${dateOnly(c.issued_on)}`)
  parts.push(isBlank(c.expires_on) ? 'no stated expiry' : `expires ${dateOnly(c.expires_on)}`)
  if (!isBlank(c.verification_status)) parts.push(`verification: ${humanize(c.verification_status)}`)
  if (c.is_synthetic !== false) parts.push('synthetic')
  const agt = asRec(c.agt)
  const agtText =
    Object.keys(agt).length > 0
      ? `Gas tester certificate: ${humanize(agt.credential_kind)}, issued ${dateOnly(agt.issued_on)}, ${
          isBlank(agt.expires_on) ? 'no stated expiry' : `expires ${dateOnly(agt.expires_on)}`
        }${isBlank(agt.verification_status) ? '' : `, verification: ${humanize(agt.verification_status)}`}`
      : ''
  return html`${parts.join(' | ')}${agtText ? html`<br>${agtText}` : EMPTY}<br><span class="mono small">credential ${c.credential_id}</span>`
}

function unsigned(expect: Expect, text = 'No electronic signature is recorded for this step.'): Html {
  const who = expect.role ? `${roleLabel(expect.role)} - ` : ''
  return html`<div class="sig sig-unsigned"><p class="sig-head"><span class="sig-mark mark-unsigned">NOT SIGNED</span><span class="sig-for">${who}${expect.what}</span></p><p class="sig-empty">${text}</p></div>`
}

function signature(a: AttestationView | null | undefined, expect: Expect, missingText?: string): Html {
  if (!a) return unsigned(expect, missingText)
  const signer = a.signer
  const label = capacityLabel(a.declared_capacity)
  const capacity = isBlank(a.declared_capacity) || label === humanize(a.declared_capacity) ? label : `${label} (${str(a.declared_capacity)})`
  return html`<div class="sig sig-signed">
<p class="sig-head"><span class="sig-mark mark-signed">SIGNED</span><span class="sig-for">${expect.what}</span>${a.is_synthetic !== false ? tag('synthetic', 'syn') : EMPTY}</p>
<p class="sig-name">${signer?.display_name || 'Signer name not recorded'}${signer?.organisation ? html` <span class="sig-org">${signer.organisation}</span>` : EMPTY}</p>
<dl class="sig-kv">
<dt>Role</dt><dd>${roleLabel(a.signing_role)}</dd>
<dt>Server time</dt><dd>${muscat(a.server_signed_at, true)}</dd>
<dt>Capacity</dt><dd>${capacity}</dd>
<dt>Content digest</dt><dd>${mono(a.content_digest)}</dd>
<dt>Credential</dt><dd class="wide">${credentialText(a.credential)}</dd>
<dt>Record ids</dt><dd class="wide"><span class="mono small">signature ${a.id}${a.policy_version_id ? ` | rules ${a.policy_version_id}` : ''}${signer?.id ? ` | person ${signer.id}` : ''}</span></dd>
</dl>
</div>`
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------
type StateStamp = { tone: 'info' | 'warn' | 'bad'; short: string; title: string; text: string }

type Ctx = {
  b: AnyBundle
  live: PermitBundle | null
  bundleVersion: string | null
  opts: RenderOptions
  synthetic: boolean
  staging: boolean
  policyApproved: boolean
  state: string | null
  stamp: StateStamp
  revisions: RevisionView[]
  current: RevisionView | null
  cycles: CycleView[]
  revNo: Map<string, number>
  cycleNo: Map<string, number>
  gasNo: Map<string, string>
  talkNo: Map<string, string>
  stopNo: Map<string, string>
  people: Map<string, PersonRef>
  areaNames: Map<string, string>
  attestations: AttestationView[]
}

const STATE_STAMPS: Record<string, StateStamp> = {
  draft: { tone: 'warn', short: 'DRAFT - NOT AUTHORISED', title: 'Draft - not authorised', text: 'This application has not been submitted. It is not a permit to work.' },
  returned_for_revision: { tone: 'warn', short: 'RETURNED - NOT AUTHORISED', title: 'Returned for revision - not authorised', text: 'The Responsible Supervisor returned this application for changes. It is not a permit to work.' },
  submitted: { tone: 'warn', short: 'IN REVIEW - NOT AUTHORISED', title: 'Submitted for review - not authorised', text: 'This application is waiting for the permit meeting review. It is not a permit to work.' },
  reviewed: { tone: 'warn', short: 'REVIEWED - NOT AUTHORISED', title: 'Reviewed - not yet authorised', text: 'This application is waiting for RS authorisation. It is not a permit to work.' },
  authorised: { tone: 'info', short: 'AUTHORISED', title: 'Authorised permit - check the live permit before work', text: 'Before any work the Operations Manager approves the authorisation (where the policy requires it); each day also needs RS pre-issue, RS validation and PH acceptance. This paper copy does not show whether work may proceed now: check the live permit before starting or restarting work.' },
  cancellation_signed: { tone: 'bad', short: 'CANCELLED - NO WORK', title: 'Cancelled by the Permit Holder - no work allowed', text: 'No work is allowed under this permit. RS closeout is pending.' },
  closed_out: { tone: 'bad', short: 'CLOSED OUT - NO WORK', title: 'Closed out - no work allowed', text: 'Cancelled by the Permit Holder and closed out after the worksite inspection. Archive pending.' },
  archived: { tone: 'bad', short: 'ARCHIVED - NO WORK', title: 'Archived record - no work allowed', text: 'Cancelled, closed out and archived. This copy is a record only.' },
}

const NOT_AUTHORISED_STATES = ['draft', 'returned_for_revision', 'submitted', 'reviewed']

function stateStamp(state: string | null, b: AnyBundle): StateStamp {
  if (state && STATE_STAMPS[state]) return STATE_STAMPS[state]
  if (state) return { tone: 'warn', short: humanize(state).toUpperCase(), title: `State: ${humanize(state)}`, text: 'Check the live permit before relying on this copy.' }
  if (b.closeout) {
    return { tone: 'bad', short: 'CLOSED OUT - NO WORK', title: 'Closed-out record - no work allowed', text: 'Cancelled by the Permit Holder and closed out after the worksite inspection. This copy is a record only.' }
  }
  if (b.cancellation) {
    return { tone: 'bad', short: 'CANCELLED - NO WORK', title: 'Cancelled by the Permit Holder - no work allowed', text: 'No work is allowed under this permit.' }
  }
  return { tone: 'warn', short: 'STATUS NOT STATED', title: 'Status not stated in this copy', text: 'Check the live permit before relying on this copy.' }
}

function liveOf(b: AnyBundle): PermitBundle | null {
  const o = b as Partial<PermitBundle>
  return o.viewer !== undefined || o.eligibility !== undefined || o.generated_at !== undefined || o.draft !== undefined || o.archive !== undefined
    ? (b as PermitBundle)
    : null
}

function collectAttestations(b: AnyBundle): AttestationView[] {
  const out: AttestationView[] = []
  const seen = new Set<string>()
  const add = (a: AttestationView | null | undefined) => {
    if (!a) return
    const key = typeof a.id === 'string' && a.id !== '' ? a.id : `#${out.length}`
    if (seen.has(key)) return
    seen.add(key)
    out.push(a)
  }
  arr(b.revisions).forEach((r) => add(r.submission))
  arr(b.reviews).forEach((r) => add(r.attestation))
  arr(b.approvals).forEach((r) => add(r.attestation))
  arr(b.examinations).forEach((r) => add(r.attestation))
  arr(b.class_a?.validation_conditions).forEach((r) => add(r.attestation))
  arr(b.supporting_records).forEach((r) => {
    add(r.recorded)
    arr(r.verifications).forEach((v) => add(v.attestation))
  })
  arr(b.authorisations).forEach((r) => add(r.attestation))
  arr(b.om_approvals).forEach((r) => add(r.attestation))
  arr(b.extension_requests).forEach((r) => {
    add(r.requested)
    add(r.decision?.attestation)
  })
  arr(b.cycles).forEach((c) => {
    add(c.preissue?.attestation)
    add(c.validation?.attestation)
    arr(c.examinations).forEach((x) => add(x.attestation))
    arr(c.gas_tests).forEach((g) => add(g.attestation))
    arr(c.toolbox_talks).forEach((t) => {
      add(t.giver)
      arr(t.attendees).forEach((x) => add(x.signed))
    })
    add(c.acceptance?.attestation)
    arr(c.suspensions).forEach((s) => {
      add(s.ph_attestation)
      arr(s.confirmations).forEach((x) => add(x.attestation))
    })
  })
  add(b.cancellation?.attestation)
  add(b.closeout?.attestation)
  return sorted(out, (x, y) => cmpTime(x.server_signed_at, y.server_signed_at) || cmpText(x.id, y.id))
}

function buildContext(b: AnyBundle, opts: RenderOptions): Ctx {
  const live = liveOf(b)
  const bundleVersion = typeof (b as Partial<ArchiveBundle>).bundle_version === 'string' ? (b as ArchiveBundle).bundle_version : null
  const permit = asRec(b.permit)
  const policy = asRec(b.policy)
  const state = typeof permit.lifecycle_state === 'string' && permit.lifecycle_state !== '' ? permit.lifecycle_state : null
  const attestations = collectAttestations(b)

  const synthetic =
    permit.is_synthetic !== false ||
    policy.status === 'synthetic_fixture' ||
    /^SYN/i.test(str(policy.profile_code)) ||
    attestations.some((a) => a.is_synthetic !== false || (a.credential ? a.credential.is_synthetic !== false : false))
  const policyApproved = policy.status === 'approved'

  const revisions = sorted(arr(b.revisions), (x, y) => cmpNum(x.revision_number, y.revision_number))
  const current =
    revisions.find((r) => r.id === permit.current_revision_id) ?? (revisions.length > 0 ? revisions[revisions.length - 1] : null)
  const cycles = sorted(arr(b.cycles), (x, y) => cmpNum(x.cycle_number, y.cycle_number) || cmpTime(x.created_at, y.created_at))

  const revNo = new Map<string, number>()
  revisions.forEach((r) => revNo.set(r.id, r.revision_number))
  const cycleNo = new Map<string, number>()
  cycles.forEach((c) => cycleNo.set(c.id, c.cycle_number))

  const gasNo = new Map<string, string>()
  const talkNo = new Map<string, string>()
  const stopNo = new Map<string, string>()
  cycles.forEach((c) => {
    sorted(arr(c.gas_tests), (x, y) => cmpTime(x.recorded_at, y.recorded_at) || cmpText(x.id, y.id)).forEach((g) => gasNo.set(g.id, `G${gasNo.size + 1}`))
    sorted(arr(c.toolbox_talks), (x, y) => cmpTime(x.giver?.server_signed_at, y.giver?.server_signed_at) || cmpText(x.id, y.id)).forEach((t) =>
      talkNo.set(t.id, `T${talkNo.size + 1}`),
    )
    sorted(arr(c.suspensions), (x, y) => cmpTime(x.server_received_at, y.server_received_at) || cmpText(x.id, y.id)).forEach((s) =>
      stopNo.set(s.id, `S${stopNo.size + 1}`),
    )
  })

  const people = new Map<string, PersonRef>()
  const addPerson = (p: PersonRef | null | undefined) => {
    if (p && typeof p.id === 'string' && p.id !== '' && !people.has(p.id)) people.set(p.id, p)
  }
  addPerson(asRec(b.permit).created_by as PersonRef | undefined)
  attestations.forEach((a) => addPerson(a.signer))
  revisions.forEach((r) => arr(r.parties).forEach((p) => addPerson(p.person)))
  cycles.forEach((c) => {
    addPerson(c.requested_by)
    arr(c.toolbox_talks).forEach((t) => arr(t.attendees).forEach((x) => addPerson(x.person)))
    arr(c.suspensions).forEach((s) => addPerson(s.reporter))
  })
  arr(b.scope_changes).forEach((s) => addPerson(s.reporter))
  arr(b.events).forEach((e) => addPerson(e.actor))
  if (live?.draft) addPerson(live.draft.updated_by)

  const areaNames = new Map<string, string>()
  const addArea = (a: { id?: string; name?: string; code?: string } | null | undefined) => {
    if (a && typeof a.id === 'string' && a.id !== '' && !areaNames.has(a.id)) areaNames.set(a.id, `${a.name ?? 'Area'}${a.code ? ` (${a.code})` : ''}`)
  }
  addArea(b.permit?.site_area)
  revisions.forEach((r) => {
    addArea(r.site_area)
    arr(r.requirements).forEach((q) => {
      if (q.requirement_kind === 'oac_agreement') addArea(q.detail?.area)
    })
  })
  arr(b.approvals).forEach((a) => addArea(a.affected_site_area))

  return {
    b,
    live,
    bundleVersion,
    opts,
    synthetic,
    staging: isStagingRecord(b as { permit?: { permit_number?: string | null } }, opts.environmentKind),
    policyApproved,
    state,
    stamp: stateStamp(state, b),
    revisions,
    current,
    cycles,
    revNo,
    cycleNo,
    gasNo,
    talkNo,
    stopNo,
    people,
    areaNames,
    attestations,
  }
}

function shortId(id: unknown): string {
  const s = str(id)
  return s.length > 8 ? s.slice(0, 8) : s
}

function revRef(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return '-'
  const n = ctx.revNo.get(str(id))
  return n !== undefined ? `Revision ${n}` : `Revision ${shortId(id)} (not in this copy)`
}

function cycleRef(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return '-'
  const n = ctx.cycleNo.get(str(id))
  return n !== undefined ? `Cycle ${n}` : `Cycle ${shortId(id)} (not in this copy)`
}

function gasRef(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return '-'
  return ctx.gasNo.get(str(id)) ? `Gas test ${ctx.gasNo.get(str(id))}` : `Gas test ${shortId(id)} (not in this copy)`
}

function authRef(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return 'no authorisation recorded'
  const a = arr(ctx.b.authorisations).find((x) => x.id === id)
  return a ? `authorisation signed ${muscat(a.attestation?.server_signed_at)}` : `authorisation ${shortId(id)} (not in this copy)`
}

/** Names an id that appears in free-form details, when this copy knows it. */
function idLabel(ctx: Ctx, value: unknown): string | null {
  if (typeof value !== 'string' || value.length < 32) return null
  if (ctx.gasNo.has(value)) return `gas test ${ctx.gasNo.get(value)}`
  if (ctx.talkNo.has(value)) return `toolbox talk ${ctx.talkNo.get(value)}`
  if (ctx.stopNo.has(value)) return `stop ${ctx.stopNo.get(value)}`
  if (ctx.revNo.has(value)) return `revision ${ctx.revNo.get(value)}`
  if (ctx.cycleNo.has(value)) return `cycle ${ctx.cycleNo.get(value)}`
  if (ctx.areaNames.has(value)) return ctx.areaNames.get(value) ?? null
  if (ctx.people.has(value)) return ctx.people.get(value)?.display_name ?? null
  return null
}

function personById(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return '-'
  const p = ctx.people.get(str(id))
  return p ? personText(p) : `Person ${shortId(id)} (name not in this copy)`
}

function areaById(ctx: Ctx, id: unknown): string {
  if (isBlank(id)) return '-'
  return ctx.areaNames.get(str(id)) ?? `Area ${shortId(id)} (name not in this copy)`
}

// -----------------------------------------------------------------------------
// Document frame
// -----------------------------------------------------------------------------
function classKey(permitClass: unknown): 'cls-b' | 'cls-a' | 'cls-x' {
  return permitClass === 'B' ? 'cls-b' : permitClass === 'A' ? 'cls-a' : 'cls-x'
}

function classBandText(permitClass: unknown): string {
  if (permitClass === 'B') return 'CLASS B PERMIT - BLUE - MEDIUM RISK'
  if (permitClass === 'A') return 'CLASS A PERMIT - RED - HIGH RISK'
  return 'PERMIT CLASS NOT STATED'
}

function watermarkText(ctx: Ctx): string | null {
  if (ctx.synthetic) return SYNTHETIC_WATERMARK
  if (ctx.staging) return STAGING_WATERMARK
  if (!ctx.policyApproved) return UNAPPROVED_WATERMARK
  if (ctx.state && NOT_AUTHORISED_STATES.indexOf(ctx.state) >= 0) return NOT_AUTHORISED_WATERMARK
  return null
}

/** A permit that is not yet authorised also carries a large DRAFT mark (0015). */
function isDraftCopy(ctx: Ctx): boolean {
  if (ctx.state) return NOT_AUTHORISED_STATES.indexOf(ctx.state) >= 0
  return arr(ctx.b.authorisations).length === 0 && !ctx.b.cancellation && !ctx.b.closeout
}

function draftWatermark(): Html {
  return html`<div class="draft-wm" aria-hidden="true">DRAFT</div>`
}

/** Two fixed lines ('... -' / 'NOT A PERMIT TO WORK'); the element's text is the whole phrase. */
function watermark(text: string, draft = false): Html {
  const at = text.indexOf(' - ')
  const lines = at > 0 ? [`${text.slice(0, at)} -`, text.slice(at + 3)] : [text]
  return html`<div class="${draft ? 'watermark with-draft' : 'watermark'}" aria-hidden="true">${joinHtml(
    lines.map((l) => html`<span class="wm-line">${l}</span>`),
    html` `,
  )}</div>`
}

function runningHeader(ctx: Ctx): Html {
  const p = ctx.b.permit
  return html`<div class="run-head ${classKey(p?.permit_class)}"><span class="rh-no">${p?.permit_number || 'No permit number'}</span><span class="rh-class">Class ${p?.permit_class || '?'}</span><span class="rh-state">${ctx.stamp.short}</span>${
    ctx.synthetic ? html`<span class="rh-syn">SYNTHETIC - NOT A PERMIT TO WORK</span>` : ctx.staging ? html`<span class="rh-syn">STAGING - NOT A PERMIT TO WORK</span>` : EMPTY
  }<span class="rh-title">${p?.title || ''}</span></div>`
}

function runningFooter(ctx: Ctx): Html {
  const p = ctx.b.permit
  return html`<div class="run-foot">BadrEOR ePTW | ${p?.permit_number || '-'} | ${ctx.opts.mode === 'archive' ? 'Archive rendering' : 'Printed copy'} | template ${RENDER_TEMPLATE_VERSION} | generated ${
    ctx.opts.generatedAt ? muscat(ctx.opts.generatedAt, true) : 'time not stated'
  }</div>`
}

function part(ctx: Ctx, content: readonly Html[]): Html {
  return html`<table class="part" role="presentation">
<thead><tr><td>${runningHeader(ctx)}</td></tr></thead>
<tfoot><tr><td>${runningFooter(ctx)}</td></tr></tfoot>
<tbody><tr><td>${content}</td></tr></tbody>
</table>`
}

function section(no: number, body: Html | readonly Html[]): Html {
  const s = PERMIT_SECTIONS.find((x) => x.no === no)
  const title = s ? s.title : `Section ${no}`
  return html`<section class="sec" id="sec-${no}"><h2 class="sec-title"><span class="sec-no">${no}</span><span class="sec-name">${title}</span><span class="sec-signer">${
    s ? `Sign-off: ${s.signer}` : ''
  }</span></h2>${body}</section>`
}

function qrBlock(opts: RenderOptions): Html {
  if (isBlank(opts.qrSvg) && isBlank(opts.locatorUrl)) return EMPTY
  let src = ''
  if (typeof opts.qrSvg === 'string' && opts.qrSvg.trim() !== '') {
    try {
      // As an image the SVG cannot run script or load anything, whatever it contains.
      src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(opts.qrSvg)}`
    } catch {
      src = ''
    }
  }
  return html`<div class="qr-box">${src ? html`<img class="qr" alt="QR code that opens this permit in the ePTW app" src="${src}">` : EMPTY}<p class="qr-note">${QR_NOTE}</p>${
    isBlank(opts.locatorUrl) ? EMPTY : html`<p class="qr-url">${opts.locatorUrl}</p>`
  }</div>`
}

function latestAuthorisation(ctx: Ctx) {
  const all = sorted(arr(ctx.b.authorisations), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at))
  const permitAuth = all.find((a) => a.id === ctx.b.permit?.current_authorisation_id)
  return permitAuth ?? (all.length > 0 ? all[all.length - 1] : null)
}

/** Header rows for an extended permit or an open extension request (0020); none otherwise. */
function extensionHeaderRows(ctx: Ctx): Row[] {
  const s = extensionSummary(ctx.b)
  if (!s) return []
  const rows: Row[] = []
  if (s.extended) rows.push(['Extended (RS)', `From the original end ${muscat(s.originalTo)} to ${muscat(s.currentTo)}`])
  if (s.open && !ctx.b.cancellation) {
    rows.push(['Extension requested', html`<span class="missing">To ${muscat(s.open.requested_to)} - waiting for the RS; not in force</span>`])
  }
  return rows
}

/** Header row for the OM approval; none when the permit's policy has no OM step. */
function omHeaderRow(ctx: Ctx): Row[] {
  const status = omApprovalStatus(ctx.b)
  if (status === 'not_required') return []
  const a = currentOmApproval(ctx.b)
  return [[
    'Operations Manager approval',
    a ? `${personText(a.attestation?.signer)} at ${muscat(a.attestation?.server_signed_at)}` : html`<span class="missing">Pending - no work may start until the OM approves</span>`,
  ]]
}

function headerBlock(ctx: Ctx): Html {
  const b = ctx.b
  const p = b.permit
  const policy = b.policy
  const authority = b.authority
  const auth = latestAuthorisation(ctx)
  const cycle = ctx.cycles.find((c) => c.id === p?.current_cycle_id) ?? (ctx.cycles.length > 0 ? ctx.cycles[ctx.cycles.length - 1] : null)
  const policyText = policy
    ? `${policy.version_label || '-'} - ${policyStatusLabel(policy.status)}${policy.profile_name ? ` - ${policy.profile_name}` : ''}${policy.profile_code ? ` (${policy.profile_code})` : ''}`
    : 'Rules version not stated - not approved policy'
  const authorityText = authority
    ? `${authority.issuer || '-'} - ${humanize(authority.authority_mode)}, ${humanize(authority.scope_mode)} scope${
        authority.approval_evidence_reference ? ` - evidence: ${authority.approval_evidence_reference}` : ' - no approval evidence reference'
      }`
    : null
  const validity: Row[] = [
    ['Authorised period', auth ? `${windowText(auth.authorisation_from, auth.authorisation_to)} (${revRef(ctx, auth.revision_id)})` : 'Not authorised'],
    ...extensionHeaderRows(ctx),
    ...omHeaderRow(ctx),
    ['Latest daily cycle', cycle ? `Cycle ${cycle.cycle_number} - ${cycleStateLabel(cycle.cycle_state)}` : 'No daily cycle'],
    ['Validated for', cycle?.validation ? windowText(cycle.validation.validation_from, cycle.validation.validation_to) : 'Not validated'],
    ['Accepted by the PH', cycle?.acceptance ? `${personText(cycle.acceptance.attestation?.signer)} at ${muscat(cycle.acceptance.attestation?.server_signed_at)}` : 'Not accepted'],
    ['PH cancellation', b.cancellation ? `Signed at ${muscat(b.cancellation.attestation?.server_signed_at)} - no work after this` : 'Not cancelled'],
  ]
  return html`<header class="doc-head">
<div class="band ${classKey(p?.permit_class)}">${classBandText(p?.permit_class)}</div>
<div class="head-grid">
<div class="head-main">
<p class="org"><img class="logo" alt="BadrEOR logo" width="${String(Math.round(BADREOR_LOGO_WIDTH / 4))}" height="${String(Math.round(BADREOR_LOGO_HEIGHT / 4))}" src="${BADREOR_LOGO_DATA_URI}"><span>BadrEOR - Electronic Permit to Work</span></p>
<h1 class="pno">${p?.permit_number || 'No permit number'}</h1>
<p class="ptitle">${p?.title || 'Untitled permit'}</p>
${kv([
  ['Class', classLabel(p?.permit_class)],
  ['Geographical area', p?.site ? `${p.site.name} (${p.site.code})` : null],
  ['Unit', p?.site_area ? `${p.site_area.name} (${p.site_area.code})` : null],
  ['Work type', p?.work_type ? `${p.work_type.code} - ${p.work_type.description} (${p.work_type.activity_group})` : null],
  ['Rules version and status', policyText],
  ['Issuing authority', authorityText],
])}
</div>
${qrBlock(ctx.opts)}
</div>
<div class="stamp tone-${ctx.stamp.tone}"><p class="stamp-title">${ctx.stamp.title}</p><p class="stamp-text">${ctx.stamp.text}</p><p class="stamp-text">This copy never shows whether work may proceed now. The server decides that live; open the permit in the ePTW app.</p></div>
${ctx.synthetic ? html`<div class="syn-note">${SYNTHETIC_NOTICE}</div>` : EMPTY}
${ctx.staging ? html`<div class="syn-note">${STAGING_NOTICE}</div>` : EMPTY}
${!ctx.policyApproved ? html`<div class="syn-note">Rules version ${policy?.version_label || '(not stated)'} is not approved BadrEOR policy (${policyStatusLabel(policy?.status)}).</div>` : EMPTY}
<h3 class="sub">Validity</h3>
${kv(validity)}
<p class="tz-note">All times are Asia/Muscat (GST, UTC+4) from the server clock. "Reported" times are what the person stated; "server" times are when the record was made.</p>
</header>`
}

// -----------------------------------------------------------------------------
// Sections 1-6: the application (current revision, superseded revisions, or a draft)
// -----------------------------------------------------------------------------
type AppSource = { kind: 'revision' | 'draft'; revision: RevisionView | null; fields: Rec; main: boolean }

const KNOWN_FIELDS = new Set([
  'title', 'class_basis', 'site_area_id', 'work_type_id', 'location_detail', 'work_order_ref', 'requested_from',
  'requested_to', 'work_description', 'work_boundary', 'tools_equipment', 'work_party_max', 'nominated_ph_person_id',
  'urgency_justification', 'tasks', 'hazards', 'jhsep', 'simops', 'gas_plan', 'isolation_required', 'isolation', 'hot_work',
  'confined_space_entry', 'excavation', 'vehicle_entry', 'supporting_documents', 'affected_area_ids',
  'location_attributes_confirmed', 'area_classification', 'class', 'policy_version_label',
  // 0015 form review
  'third_party', 'job_readiness', 'location_classification', 'location_classification_label',
  'class_activities', 'class_activities_detail',
])

const THIRD_PARTY_ITEMS: ReadonlyArray<readonly [string, string]> = [
  ['hm_jsa_approved', 'Their hazard management plan or job safety analysis (HM/JSA) is approved'],
  ['procedures_approved', 'Their procedures are approved'],
  ['equipment_approved', 'Their equipment is approved'],
  ['personnel_approved', 'Their personnel are approved and authorised'],
]

const READINESS_ITEMS: ReadonlyArray<readonly [string, string]> = [
  ['emergency_response_adequate', 'Emergency response provisions'],
  ['ppe_adequate', 'Personal protective equipment (PPE)'],
  ['personnel_credentials_adequate', "Personal credentials - the people's competence and certificates for the job"],
  ['equipment_credentials_adequate', 'Equipment credentials - certification and inspection of the equipment used'],
]

/** The chosen class activities: the sealed rows when present, else the catalogue's (note 20). */
function activitiesCell(f: Rec): Cell {
  const codes = arr(f.class_activities as unknown[] | undefined).map((x) => str(x))
  if (codes.length === 0) return 'None chosen'
  const sealed = arr(f.class_activities_detail as unknown[] | undefined).map(asRec)
  return codes
    .map((code) => {
      const s = sealed.find((d) => d.code === code)
      if (s && !isBlank(s.label)) return `${str(s.label)} (Class ${str(s.permit_class)}, p. ${str(s.source_page)})`
      const c = CLASS_ACTIVITIES.find((a) => a.code === code)
      return c ? `${c.label} (Class ${c.permit_class}, p. ${c.page})` : `${code} (not in the list)`
    })
    .join('; ')
}

/** Confirmed / Not confirmed, never blank (each must be ticked deliberately). */
function confirmed(value: unknown): Html {
  return value === true ? tag('Confirmed', 'ok') : tag('Not confirmed', 'bad')
}

function partyOf(rev: RevisionView | null, role: 'PA' | 'PH') {
  return rev ? arr(rev.parties).find((p) => p.role_code === role) ?? null : null
}

function valueCell(value: unknown): Cell {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  return stableJson(value)
}

function appApplication(ctx: Ctx, src: AppSource): Html {
  const f = src.fields
  const rev = src.revision
  const pa = partyOf(rev, 'PA')
  const ph = partyOf(rev, 'PH')
  const rows: Row[] = [
    ['Title', valueCell(f.title)],
    ['Permit class', classLabel(f.class ?? ctx.b.permit?.permit_class)],
    ['Why this class: the activities in this work (SP-2329 Appendix 1)', activitiesCell(f)],
    ...(isBlank(f.class_basis) ? [] : ([['Class basis (free text, earlier form)', valueCell(f.class_basis)]] as Row[])),
    ['Work order reference', valueCell(f.work_order_ref)],
    [
      'Date of application',
      rev ? `${muscat(rev.submission?.server_signed_at ?? rev.sealed_at)} (server time of submission)` : 'Recorded when the application is submitted',
    ],
    ['Date of initiation (planned start)', muscat(f.requested_from)],
    ['Requested start', muscat(f.requested_from)],
    ['Requested end', muscat(f.requested_to)],
    ['Urgency justification (short notice)', isBlank(f.urgency_justification) ? 'None given' : valueCell(f.urgency_justification)],
    ['Work description', valueCell(f.work_description)],
    ['Work boundary', valueCell(f.work_boundary)],
    ['Tools and equipment', valueCell(f.tools_equipment)],
    ['Most people in the work party', valueCell(f.work_party_max)],
    ['Permit Applicant (PA)', pa ? personText(pa.person, pa.organisation) : src.kind === 'draft' ? `Draft last edited by ${personText(ctx.live?.draft?.updated_by)}` : null],
    ['Nominated Permit Holder (PH)', ph ? personText(ph.person, ph.organisation) : personById(ctx, f.nominated_ph_person_id)],
  ]
  if (!isBlank(f.policy_version_label)) rows.push(['Rules version when submitted', valueCell(f.policy_version_label)])
  if (rev) {
    rows.push(['Revision', `Revision ${rev.revision_number}, sealed ${muscat(rev.sealed_at, true)}`])
    rows.push(['Scope digest', mono(rev.scope_digest)])
    rows.push(['Supersedes', rev.supersedes_revision_id ? revRef(ctx, rev.supersedes_revision_id) : 'None'])
  }
  if (src.kind === 'draft' && ctx.live?.draft) {
    rows.push(['Draft version', `${ctx.live.draft.draft_version}, last edited ${muscat(ctx.live.draft.updated_at, true)}`])
    if (ctx.live.draft.based_on_revision_id) rows.push(['Draft based on', revRef(ctx, ctx.live.draft.based_on_revision_id)])
  }
  if (src.main) {
    const p = ctx.b.permit
    rows.push(['Permit created', `${muscat(p?.created_at, true)} by ${personText(p?.created_by)}`])
    rows.push(['Replaces permit', p?.replaces_permit_id ? mono(p.replaces_permit_id) : 'None'])
  }
  const parts: Html[] = [kv(rows)]
  // Third party or subcontractor (0015; revisions sealed earlier never asked it).
  if (f.third_party !== undefined && f.third_party !== null) {
    const tp = asRec(f.third_party)
    parts.push(h3('Third party or subcontractor'))
    parts.push(
      grid(
        ['Question', 'Answer'],
        [
          ['Is a third party or subcontractor part of the activity?', yesNo(tp.involved)],
          ...(tp.involved === true ? THIRD_PARTY_ITEMS.map(([k, label]) => [label, confirmed(tp[k])] as Cell[]) : []),
        ],
      ),
    )
  }
  if (src.main && ctx.revisions.length > 0) {
    const returned = new Map<string, string>()
    arr(ctx.b.reviews).forEach((r) => {
      if (r.review_kind === 'return_for_revision') returned.set(r.revision_id, `Returned: ${r.reason || 'no reason recorded'}`)
    })
    parts.push(h3('Revision history'))
    parts.push(
      grid(
        ['Revision', 'Sealed (server)', 'Submitted by', 'Scope digest', 'Status'],
        ctx.revisions.map((r) => [
          String(r.revision_number),
          muscat(r.sealed_at, true),
          personText(r.submission?.signer),
          mono(r.scope_digest),
          `${r.id === ctx.current?.id ? 'Current' : 'Superseded'}${returned.has(r.id) ? ` - ${returned.get(r.id)}` : ''}`,
        ]),
      ),
    )
  }
  const extra = Object.keys(f)
    .filter((k) => !KNOWN_FIELDS.has(k))
    .sort()
  if (extra.length > 0) {
    parts.push(h3('Other recorded fields'))
    parts.push(kv(extra.map((k) => [k, valueCell(f[k])] as Row)))
  }
  parts.push(
    rev
      ? signature(rev.submission, { role: 'PA', what: 'Application and Job HSE plan complete' })
      : unsigned({ role: 'PA', what: 'Application and Job HSE plan complete' }, 'Not submitted. The PA signs when submitting the application.'),
  )
  return joinHtml(parts)
}

const ROUTE_QUESTIONS: ReadonlyArray<readonly [string, string]> = [
  ['isolation_required', 'Isolation of plant or equipment needed'],
  ['hot_work', 'Hot work'],
  ['confined_space_entry', 'Confined-space entry'],
  ['excavation', 'Excavation'],
  ['vehicle_entry', 'Vehicle entry to the worksite'],
]

const CLASSIFICATION_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['location_type', 'Location type'],
  ['hazardous_zone', 'Hazardous area zone'],
  ['h2s_classification', 'H2S classification'],
  ['h2s_expected_max_ppm', 'Expected maximum H2S'],
  ['critical_sour', 'Critical sour'],
  ['restricted', 'Restricted area'],
  ['norm_restricted', 'NORM restricted'],
  ['classification_source', 'Classification source'],
  ['classification_revision', 'Classification revision'],
  ['classification_status', 'Classification status'],
]

function appLocation(ctx: Ctx, src: AppSource): Html {
  const f = src.fields
  const rev = src.revision
  const p = ctx.b.permit
  const area = rev?.site_area ?? p?.site_area
  const wt = rev?.work_type ?? p?.work_type
  const parts: Html[] = [
    kv([
      ['Geographical area', p?.site ? `${p.site.name} (${p.site.code})` : null],
      ['Unit', area ? `${area.name} (${area.code})` : null],
      ['Location detail', valueCell(f.location_detail)],
      ['Work type', wt ? `${wt.code} - ${wt.description}` : null],
      ['Activity group', wt ? wt.activity_group : null],
      ['PA confirmed the location attributes', yesNo(f.location_attributes_confirmed, 'Not recorded')],
    ]),
  ]
  const ac = asRec(rev?.area_classification ?? f.area_classification)
  parts.push(h3('Area classification'))
  if (typeof f.location_classification === 'string' && f.location_classification.trim() !== '') {
    // 0015: the unit's classification from the register, sealed with the revision (code and label).
    parts.push(
      kv([
        ['Unit work-location classification (sealed)', valueCell(isBlank(f.location_classification_label) ? f.location_classification : f.location_classification_label)],
        ['Classification code', mono(f.location_classification)],
      ]),
    )
  } else if (src.kind === 'draft') {
    parts.push(note("The unit's work-location classification is sealed into the record when the application is submitted."))
  } else if (Object.keys(ac).length === 0) {
    parts.push(note('No area classification recorded.'))
  } else {
    const known = new Set(CLASSIFICATION_KEYS.map(([k]) => k))
    const rows: Row[] = CLASSIFICATION_KEYS.map(([k, label]) => {
      const v = ac[k]
      if (typeof v === 'boolean') return [label, yesNo(v, 'Not recorded')] as Row
      if (k === 'h2s_expected_max_ppm') return [label, typeof v === 'number' ? `${v} ppm` : valueCell(v)] as Row
      if (k === 'location_type' || k === 'hazardous_zone' || k === 'h2s_classification' || k === 'classification_status') {
        return [label, isBlank(v) ? null : `${humanize(v)} (${str(v)})`] as Row
      }
      return [label, valueCell(v)] as Row
    })
    Object.keys(ac)
      .filter((k) => !known.has(k))
      .sort()
      .forEach((k) => rows.push([k, valueCell(ac[k])]))
    parts.push(kv(rows))
  }
  parts.push(h3('Other affected units'))
  const oacReqs = rev ? arr(rev.requirements).filter((q) => q.requirement_kind === 'oac_agreement') : []
  const areaIds = arr(f.affected_area_ids as unknown[] | undefined).map((x) => str(x))
  if (oacReqs.length > 0) {
    parts.push(
      grid(
        ['#', 'Affected area', 'Needs'],
        oacReqs.map((q) => {
          const d = asRec(q.detail)
          const named = asRec(d.area)
          return [String(q.sequence), isBlank(named.name) ? areaById(ctx, d.site_area_id) : `${str(named.name)}${named.code ? ` (${str(named.code)})` : ''}`, 'Other affected custodian agreement (section 7)']
        }),
      ),
    )
  } else if (areaIds.length > 0) {
    parts.push(grid(['#', 'Affected area'], areaIds.map((id, i) => [String(i + 1), areaById(ctx, id)])))
  } else {
    parts.push(note('None recorded.'))
  }
  parts.push(h3('Hazardous activities in this work'))
  parts.push(grid(['Question', 'Answer'], ROUTE_QUESTIONS.map(([k, label]) => [label, yesNo(f[k])])))
  if (f.isolation_required === true) {
    // 0031: the isolation the work needs. Recording the certificate is not the physical isolation.
    const iso = asRec(f.isolation)
    const kinds = arr(iso.kinds as unknown[] | undefined).map((k) => optionLabel(ISOLATION_KINDS, str(k)))
    const from = str(iso.planned_from)
    const to = str(iso.planned_to)
    const span = from && to ? Date.parse(to) - Date.parse(from) : NaN
    parts.push(h3('Isolation'))
    parts.push(kv([
      ['Kind of isolation', kinds.length ? kinds.join('; ') : 'Not given'],
      ['Reason for isolation (what is isolated and why)', str(iso.reason)],
      ['Isolation planned to be applied', from ? muscat(from) : 'Not given'],
      ['Isolation planned to be removed', to ? muscat(to) : 'Not given'],
      ['Planned isolation duration', Number.isNaN(span) ? 'Not given' : span > 0 ? durationLabel(span) : 'Ends before it starts'],
      ['Isolation Certificate (F08) completed', iso.certificate_documented === true ? `Yes - No. ${isBlank(iso.certificate_reference) ? 'not given' : str(iso.certificate_reference)}` : iso.certificate_documented === false ? 'Not yet - recorded and verified before authorisation (section 6)' : 'Not answered'],
    ]))
  }
  return joinHtml(parts)
}

/** Whole days and hours, as the application screen shows them. */
function durationLabel(ms: number): string {
  const hours = Math.round(ms / 3_600_000)
  const days = Math.floor(hours / 24)
  const rest = hours % 24
  const parts: string[] = []
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`)
  if (rest || !days) parts.push(`${rest} hour${rest === 1 ? '' : 's'}`)
  return parts.join(' ')
}

type TaskRow = {
  sequence: unknown
  description: unknown
  boundary: unknown
  activity_group: unknown
  equipmentRegister: string | null
  equipmentText: unknown
  is_pressure_test: unknown
  pressure_test_type: unknown
  test_medium: unknown
  surface_preparation_method: unknown
  procedure_identity: unknown
  procedure_revision: unknown
  procedure_issuer: unknown
  evidence_refs: readonly unknown[]
  evidence_status: unknown
  verification_status: unknown
}

function tasksOf(src: AppSource): TaskRow[] {
  const typed = src.revision ? arr(src.revision.tasks) : []
  if (typed.length > 0) {
    return sorted(
      typed.map((t) => ({
        sequence: t.sequence,
        description: t.description,
        boundary: t.boundary,
        activity_group: t.activity_group,
        equipmentRegister: t.equipment ? `${t.equipment.tag} - ${t.equipment.description}` : t.equipment_id ? `Equipment ${shortId(t.equipment_id)}` : null,
        equipmentText: t.equipment_text,
        is_pressure_test: t.is_pressure_test,
        pressure_test_type: t.pressure_test_type,
        test_medium: t.test_medium,
        surface_preparation_method: t.surface_preparation_method,
        procedure_identity: t.procedure_identity,
        procedure_revision: t.procedure_revision,
        procedure_issuer: t.procedure_issuer,
        evidence_refs: arr(t.procedure_evidence_refs),
        evidence_status: t.procedure_evidence_status,
        verification_status: t.procedure_verification_status,
      })),
      (x, y) => cmpNum(x.sequence, y.sequence),
    )
  }
  const raw = arr(src.fields.tasks as unknown[] | undefined).map(asRec)
  return sorted(
    raw.map((t, i) => {
      const ref = asRec(t.applicable_procedure_reference)
      return {
        sequence: typeof t.sequence === 'number' ? t.sequence : i + 1,
        description: t.description,
        boundary: t.boundary,
        activity_group: t.activity_group,
        equipmentRegister: isBlank(t.equipment_id) ? null : `Equipment ${shortId(t.equipment_id)}`,
        equipmentText: t.equipment_text,
        is_pressure_test: t.is_pressure_test,
        pressure_test_type: t.pressure_test_type,
        test_medium: t.test_medium,
        surface_preparation_method: t.surface_preparation_method,
        procedure_identity: ref.identity,
        procedure_revision: ref.revision,
        procedure_issuer: ref.issuer,
        evidence_refs: arr(t.procedure_evidence_refs as unknown[] | undefined),
        evidence_status: undefined,
        verification_status: undefined,
      }
    }),
    (x, y) => cmpNum(x.sequence, y.sequence),
  )
}

function appTasks(ctx: Ctx, src: AppSource): Html {
  const tasks = tasksOf(src)
  if (tasks.length === 0) return note('No tasks recorded.')
  return joinHtml(
    tasks.map((t) => {
      const procedure = isBlank(t.procedure_identity)
        ? 'None given'
        : `${str(t.procedure_identity)} Rev ${isBlank(t.procedure_revision) ? '-' : str(t.procedure_revision)}, issued by ${isBlank(t.procedure_issuer) ? '-' : str(t.procedure_issuer)}`
      const verification = isBlank(t.verification_status)
        ? src.kind === 'draft'
          ? 'Checked after submission'
          : 'Not recorded'
        : `${humanize(t.verification_status)}${ctx.bundleVersion ? ' (as recorded in the archive bundle)' : ' (server check when this copy was read)'}`
      // From 0015 a pressure test is a Pressure Testing task (the group asks the details);
      // a revision sealed earlier recorded the answer itself.
      const pressureTest = t.is_pressure_test === true || t.activity_group === PRESSURE_TEST_GROUP
      const rows: Row[] = [
        ['Description', valueCell(t.description)],
        ['Activity group', valueCell(t.activity_group)],
        ['Equipment (as written)', valueCell(t.equipmentText)],
      ]
      // Retired fields (task boundary, register pick, evidence references) print only where recorded.
      if (!isBlank(t.boundary)) rows.splice(1, 0, ['Task boundary (earlier form)', valueCell(t.boundary)])
      if (t.equipmentRegister) rows.push(['Equipment (register, earlier form)', t.equipmentRegister])
      rows.push(['Pressure test', pressureTest ? 'Yes' : 'No'])
      rows.push(['Pressure test type', pressureTest ? valueCell(t.pressure_test_type) : 'Not a pressure test'])
      rows.push(['Test medium (as stated)', pressureTest ? valueCell(t.test_medium) : 'Not a pressure test'])
      rows.push([
        'Surface preparation method (as stated)',
        isBlank(t.surface_preparation_method) ? (t.activity_group === 'Surface Preparation' ? null : 'Not surface preparation') : valueCell(t.surface_preparation_method),
      ])
      rows.push(['Procedure', procedure])
      if (t.evidence_refs.length > 0) rows.push(['Procedure evidence references (earlier form)', t.evidence_refs.map((x) => str(x)).join('; ')])
      rows.push(['Procedure verification', verification])
      return html`<div class="block">${h4(`Task ${str(t.sequence)}`, isBlank(t.activity_group) ? EMPTY : tag(str(t.activity_group), 'neutral'))}${kv(rows)}</div>`
    }),
  )
}

function appJhsep(_ctx: Ctx, src: AppSource): Html {
  const f = src.fields
  const j = asRec(src.revision?.jhsep ?? f.jhsep)
  const s = asRec(f.simops)
  const parts: Html[] = [h3('Job HSE plan (JHSEP)')]
  if (Object.keys(j).length === 0) {
    parts.push(note('No Job HSE plan recorded.'))
  } else {
    parts.push(
      kv([
        ['Level', valueCell(j.level)],
        ['Reference', valueCell(j.reference)],
        ['Summary', valueCell(j.summary)],
        ['MOPO reviewed', yesNo(j.mopo_reviewed, 'Not recorded')],
        ['Lessons learned reviewed', yesNo(j.lessons_reviewed, 'Not recorded')],
        ['Site examination done', yesNo(j.site_examination_done, 'Not recorded')],
        ['Site examination note', valueCell(j.site_examination_note)],
      ]),
    )
  }
  // Job readiness (0015; revisions sealed earlier never asked it).
  if (f.job_readiness !== undefined && f.job_readiness !== null) {
    const jr = asRec(f.job_readiness)
    parts.push(h3('Job readiness - verified and found adequate for this job (PA)'))
    parts.push(grid(['Provision', 'Applicant'], READINESS_ITEMS.map(([k, label]) => [label, confirmed(jr[k])] as Cell[])))
  }
  parts.push(h3('Hazards and controls'))
  const typed = src.revision ? arr(src.revision.controls) : []
  const controls =
    typed.length > 0
      ? sorted(typed, (x, y) => cmpNum(x.sequence, y.sequence)).map((c) => [String(c.sequence), c.hazard, c.control, humanize(c.control_kind)] as Cell[])
      : arr(f.hazards as unknown[] | undefined)
          .map(asRec)
          .map((h, i) => [String(i + 1), valueCell(h.hazard), valueCell(h.control), humanize(h.kind)] as Cell[])
  parts.push(controls.length > 0 ? grid(['#', 'Hazard', 'Control', 'Kind'], controls) : note('No hazards and controls recorded.'))
  parts.push(h3('SIMOPS and MOPO'))
  parts.push(
    Object.keys(s).length === 0
      ? note('No SIMOPS review recorded.')
      : kv([
          ['Nearby activities', valueCell(s.nearby_activities)],
          ['Conflicts identified', valueCell(s.conflicts_identified)],
          ['MOPO consulted', yesNo(s.mopo_consulted, 'Not recorded')],
        ]),
  )
  return joinHtml(parts)
}

function appGasPlan(_ctx: Ctx, src: AppSource): Html {
  const rev = src.revision
  const req = rev ? arr(rev.requirements).find((q) => q.requirement_kind === 'gas_testing') : undefined
  const plan = asRec(src.fields.gas_plan)
  const source = Object.keys(plan).length > 0 ? plan : asRec(req?.detail)
  if (Object.keys(source).length === 0) return note('No gas testing plan recorded.')
  const required = Object.keys(plan).length > 0 ? plan.required : req ? true : undefined
  const triggers = arr(source.triggers as unknown[] | undefined).map((x) => optionLabel(GAS_TRIGGERS, x))
  const species = arr(source.species as unknown[] | undefined).map((x) => labelWithCode(GAS_SPECIES, x))
  // Hours as entered (0015); a revision sealed earlier recorded minutes.
  const hours =
    typeof source.retest_interval_hours === 'number'
      ? source.retest_interval_hours
      : typeof source.retest_interval_minutes === 'number'
        ? Math.round((source.retest_interval_minutes / 60) * 100) / 100
        : typeof asRec(req?.detail).retest_interval_minutes === 'number'
          ? Math.round(((asRec(req?.detail).retest_interval_minutes as number) / 60) * 100) / 100
          : null
  return joinHtml([
    kv([
      ['Gas testing required', yesNo(required)],
      ['Why (triggers)', triggers.length > 0 ? triggers.join('; ') : 'None recorded'],
      ['Gases to test', species.length > 0 ? species.join('; ') : 'None recorded'],
      ['Retest interval', hours !== null ? `Every ${hours} hour${hours === 1 ? '' : 's'}` : 'Not set'],
      ['Continuous monitoring', yesNo(source.continuous_monitoring)],
      ['Notes', valueCell(source.notes)],
      ['Requirement sealed at submission', rev ? (req ? `Gas testing requirement #${req.sequence}` : 'No gas testing requirement') : 'Created at submission'],
    ]),
  ])
}

const RECORD_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' }> = {
  verified: { label: 'VERIFIED', tone: 'ok' },
  recorded: { label: 'RECORDED - NOT VERIFIED', tone: 'warn' },
  withdrawn: { label: 'WITHDRAWN', tone: 'bad' },
}

function appSupporting(ctx: Ctx, src: AppSource): Html {
  const rev = src.revision
  const parts: Html[] = []
  if (!rev) {
    const docs = arr(src.fields.supporting_documents as unknown[] | undefined).map(asRec)
    parts.push(docs.length > 0 ? grid(['#', 'Document', 'Why needed'], docs.map((d, i) => [String(i + 1), valueCell(d.kind), valueCell(d.reason)])) : note('No supporting documents listed.'))
    return joinHtml(parts)
  }
  const reqs = sorted(
    arr(rev.requirements).filter((q) => q.requirement_kind === 'supporting_document'),
    (x, y) => cmpNum(x.sequence, y.sequence),
  )
  const records = sorted(
    arr(ctx.b.supporting_records).filter((r) => r.revision_id === rev.id || (src.main && !ctx.revNo.has(r.revision_id))),
    (x, y) => cmpTime(x.recorded?.server_signed_at, y.recorded?.server_signed_at) || cmpText(x.id, y.id),
  )
  parts.push(h3('Documents required by the application'))
  parts.push(
    reqs.length > 0
      ? grid(
          ['#', 'Document', 'Why needed', 'Records entered', 'Latest status'],
          reqs.map((q) => {
            const d = asRec(q.detail)
            const linked = records.filter((r) => r.requirement_id === q.id)
            return [
              String(q.sequence),
              valueCell(d.kind),
              valueCell(d.reason),
              linked.length > 0 ? linked.map((r) => `${r.external_identifier} Rev ${r.external_revision}`).join('; ') : html`<span class="missing">No record entered</span>`,
              linked.length > 0 ? linked.map((r) => RECORD_STATUS[r.status]?.label ?? humanize(r.status)).join('; ') : '-',
            ] as Cell[]
          }),
        )
      : note('No supporting documents required.'),
  )
  parts.push(h3('Supporting records'))
  if (records.length === 0) {
    parts.push(note('No supporting record entered.'))
  }
  records.forEach((r) => {
    const st = RECORD_STATUS[r.status] ?? { label: humanize(r.status), tone: 'warn' as const }
    const req = reqs.find((q) => q.id === r.requirement_id)
    const verifications = sorted(arr(r.verifications), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id))
    const blocks: Html[] = [
      h4(`${r.record_kind} ${r.external_identifier} Rev ${r.external_revision}`, tag(st.label, st.tone)),
      kv([
        ['Document kind', r.record_kind],
        ['Identifier', r.external_identifier],
        ['Revision', r.external_revision],
        ['Issuer', r.issuer],
        ['Issued (as reported)', isBlank(r.reported_issued_at) ? null : r.reported_issued_at],
        ['Expires (as reported)', isBlank(r.reported_expires_at) ? null : r.reported_expires_at],
        ['Scope note', r.scope_note],
        ['Evidence reference', r.evidence_reference],
        ['Requirement', req ? `#${req.sequence}` : r.requirement_id ? `Requirement ${shortId(r.requirement_id)}` : 'Not linked'],
        ['Recorded against', revRef(ctx, r.revision_id)],
        ['Latest status', st.label],
      ]),
      signature(r.recorded, { role: 'PA', what: 'Supporting record entered (not a verification)' }),
    ]
    verifications.forEach((v) => {
      blocks.push(h4(v.verification_kind === 'withdrawn' ? 'Withdrawal' : 'Verification'))
      blocks.push(kv([[v.verification_kind === 'withdrawn' ? 'Reason' : 'Findings', v.findings]]))
      blocks.push(signature(v.attestation, { role: v.attestation?.signing_role, what: v.verification_kind === 'withdrawn' ? 'Supporting record withdrawn' : 'Supporting record verified' }))
    })
    if (!verifications.some((v) => v.verification_kind === 'verified') && r.status !== 'withdrawn') {
      blocks.push(unsigned({ what: 'Verification by the verifier for this document type' }))
    }
    parts.push(html`<div class="block">${blocks}</div>`)
  })
  return joinHtml(parts)
}

const APP_PARTS: ReadonlyArray<(ctx: Ctx, src: AppSource) => Html> = [appApplication, appLocation, appTasks, appJhsep, appGasPlan, appSupporting]

function mainSource(ctx: Ctx): AppSource | null {
  if (ctx.current) return { kind: 'revision', revision: ctx.current, fields: asRec(ctx.current.content), main: true }
  const draft = ctx.live?.draft
  if (draft) return { kind: 'draft', revision: null, fields: asRec(draft.fields), main: true }
  return null
}

function applicationSections(ctx: Ctx): Html[] {
  const src = mainSource(ctx)
  return APP_PARTS.map((render, i) => {
    if (!src) return section(i + 1, note('No application recorded in this copy.'))
    const banner = i === 0 && src.kind === 'draft' ? html`<div class="stamp tone-warn"><p class="stamp-title">Unsubmitted draft</p><p class="stamp-text">Not signed and not authorised. Values may still change.</p></div>` : EMPTY
    return section(i + 1, [banner, render(ctx, src)])
  })
}

// -----------------------------------------------------------------------------
// Section 7: meeting review and other affected custodians
// -----------------------------------------------------------------------------
function sectionReview(ctx: Ctx): Html {
  const b = ctx.b
  const parts: Html[] = []
  const reviews = sorted(arr(b.reviews), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id))
  parts.push(h3('Permit meeting review'))
  if (reviews.length === 0 && !ctx.current) parts.push(note('No review recorded.'))
  reviews.forEach((r) => {
    const isReturn = r.review_kind === 'return_for_revision'
    const blocks: Html[] = [
      h4(`${isReturn ? 'Returned for revision' : 'Meeting review'} - ${revRef(ctx, r.revision_id)}`, tag(r.outcome === 'accepted' ? 'ACCEPTED' : r.outcome === 'returned' ? 'RETURNED' : humanize(r.outcome).toUpperCase(), r.outcome === 'accepted' ? 'ok' : 'warn')),
      kv([
        ['Outcome', humanize(r.outcome)],
        ['Planning basis', isBlank(r.planning_basis) ? null : humanize(r.planning_basis)],
        ['RS visit required before authorisation', r.rs_pre_authorisation_visit_required === null || r.rs_pre_authorisation_visit_required === undefined ? null : yesNo(r.rs_pre_authorisation_visit_required)],
        ['Reason for return', r.reason],
        ['Size of change needed', isBlank(r.change_disposition) ? null : humanize(r.change_disposition)],
      ]),
    ]
    if (!isReturn || Object.keys(asRec(r.checklist)).length > 0) {
      blocks.push(h4('Meeting checklist'))
      blocks.push(checklist(MEETING_CHECKS, r.checklist))
    }
    const participants = arr(r.participants)
    if (participants.length > 0 || !isReturn) {
      blocks.push(h4('People at the meeting (recorded names, not signatures)'))
      blocks.push(participants.length > 0 ? grid(['Name', 'Role'], participants.map((x) => [x.name, x.role])) : note('None recorded.'))
    }
    blocks.push(signature(r.attestation, { role: 'RS', what: isReturn ? 'Returned for revision' : 'Permit meeting review' }))
    parts.push(html`<div class="block">${blocks}</div>`)
  })
  const current = ctx.current
  if (current && !reviews.some((r) => r.revision_id === current.id)) {
    parts.push(unsigned({ role: 'RS', what: `Permit meeting review - Revision ${current.revision_number}` }))
  }

  parts.push(h3('Other affected custodians (OAC)'))
  const approvals = sorted(arr(b.approvals), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id))
  const oacApprovals = approvals.filter((a) => a.approval_kind === 'oac_agreement')
  const used = new Set<string>()
  const oacReqs = ctx.current ? sorted(arr(ctx.current.requirements).filter((q) => q.requirement_kind === 'oac_agreement'), (x, y) => cmpNum(x.sequence, y.sequence)) : []
  oacReqs.forEach((q) => {
    const d = asRec(q.detail)
    const areaId = str(d.site_area_id)
    const areaText = areaById(ctx, areaId)
    const found = oacApprovals.filter((a) => a.revision_id === ctx.current?.id && a.affected_site_area?.id === areaId)
    const blocks: Html[] = [h4(`Agreement for ${areaText}`)]
    if (found.length === 0) blocks.push(unsigned({ role: 'OAC', what: `Agreement for ${areaText}` }))
    found.forEach((a) => {
      used.add(a.id)
      blocks.push(kv([
        ['Added controls', a.added_controls],
        ['Notes', a.notes],
      ]))
      blocks.push(signature(a.attestation, { role: 'OAC', what: `Agreement for ${areaText}` }))
    })
    parts.push(html`<div class="block">${blocks}</div>`)
  })
  const otherOac = oacApprovals.filter((a) => !used.has(a.id))
  otherOac.forEach((a) => {
    const areaText = a.affected_site_area ? `${a.affected_site_area.name} (${a.affected_site_area.code})` : 'an area not named in this copy'
    parts.push(
      html`<div class="block">${h4(`Agreement for ${areaText} - ${revRef(ctx, a.revision_id)}`)}${kv([
        ['Added controls', a.added_controls],
        ['Notes', a.notes],
      ])}${signature(a.attestation, { role: 'OAC', what: `Agreement for ${areaText}` })}</div>`,
    )
  })
  if (oacReqs.length === 0 && otherOac.length === 0) parts.push(note('No other affected unit is listed in the application.'))

  parts.push(h3('Production Coordinator approval'))
  const pc = approvals.filter((a) => a.approval_kind === 'pc_approval')
  if (pc.length === 0) parts.push(note(ctx.b.class_a ? 'Required for a Class A permit before the RS authorises it. None recorded.' : 'None recorded.'))
  pc.forEach((a) => {
    parts.push(html`<div class="block">${h4(`PC approval - ${revRef(ctx, a.revision_id)}`)}${kv([['Notes', a.notes]])}${signature(a.attestation, { role: 'PC', what: 'Production Coordinator approval' })}</div>`)
  })
  const otherApprovals = approvals.filter((a) => a.approval_kind !== 'oac_agreement' && a.approval_kind !== 'pc_approval')
  otherApprovals.forEach((a) => {
    parts.push(html`<div class="block">${h4(`${humanize(a.approval_kind)} - ${revRef(ctx, a.revision_id)}`)}${kv([
      ['Added controls', a.added_controls],
      ['Notes', a.notes],
    ])}${signature(a.attestation, { role: a.attestation?.signing_role, what: humanize(a.approval_kind) })}</div>`)
  })

  parts.push(h3('Worksite examination before authorisation'))
  const preExams = sorted(
    arr(b.examinations).filter((x) => isBlank(x.cycle_id)),
    (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id),
  )
  const visitRequired = reviews.some((r) => r.review_kind === 'meeting_review' && r.rs_pre_authorisation_visit_required === true && r.revision_id === ctx.current?.id)
  if (preExams.length === 0) {
    parts.push(visitRequired ? unsigned({ role: 'RS', what: 'Pre-authorisation worksite examination (required by the meeting review)' }) : note('None recorded.'))
  }
  preExams.forEach((x) => {
    parts.push(
      html`<div class="block">${h4(`${examLabel(x.exam_kind)} - ${revRef(ctx, x.revision_id)}`)}${kv([
        ['Observed (reported)', muscat(x.reported_observed_at)],
        ['Findings', x.findings],
      ])}${signature(x.attestation, { role: x.attestation?.signing_role, what: 'Worksite examination' })}</div>`,
    )
  })
  return section(7, parts)
}

// -----------------------------------------------------------------------------
// Section 8: authorisation
// -----------------------------------------------------------------------------
const OM_WHAT = 'Permit approved by the Operations Manager'

/**
 * The OM approval of one authorisation (0016): signer, time and the signed declaration.
 * Missing and required on the current authorisation: a NOT SIGNED box that says work may not
 * start. Missing on a policy without the OM step (older permits): nothing.
 */
function omApprovalBlock(ctx: Ctx, authorisationId: string): Html {
  const approvals = sorted(
    arr(ctx.b.om_approvals).filter((o) => o.authorisation_id === authorisationId),
    (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id),
  )
  if (approvals.length > 0) {
    return joinHtml(
      approvals.map(
        (o) => html`${h4('Operations Manager approval')}${kv([
          ['Declaration signed', o.declaration],
          ['Notes', o.notes],
        ])}${signature(o.attestation, { role: 'OM', what: OM_WHAT })}`,
      ),
    )
  }
  if (ctx.b.om_approval_required !== true) return EMPTY
  const kind = arr(ctx.b.authorisations).find((a) => a.id === authorisationId)?.authorisation_kind
  if (kind === 'extension' && (authorisationId !== ctx.b.permit?.current_authorisation_id || omApprovalCarriedOver(ctx.b))) {
    return note('No new Operations Manager approval: the approval of the authorisation this extension extends carries over.')
  }
  if (authorisationId !== ctx.b.permit?.current_authorisation_id) return EMPTY
  return html`${h4('Operations Manager approval', tag('PENDING', 'warn'))}${unsigned(
    { role: 'OM', what: OM_WHAT },
    'Pending: no daily cycle may be requested and no work may start under this authorisation until the Operations Manager approves it.',
  )}`
}

function sectionAuthorisation(ctx: Ctx): Html {
  const list = sorted(arr(ctx.b.authorisations), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id))
  if (list.length === 0) {
    const omNote = ctx.b.om_approval_required === true ? [note('After the RS authorises it, the Operations Manager must approve the permit before any work starts.')] : []
    return section(8, [note('This permit has not been authorised.'), unsigned({ role: 'RS', what: 'Permit authorised' }), ...omNote])
  }
  return section(8, [
    ...list.map(
      (a) =>
        html`<div class="block">${h4(`${humanize(a.authorisation_kind)} authorisation - ${revRef(ctx, a.revision_id)}`, a.id === ctx.b.permit?.current_authorisation_id ? tag('CURRENT', 'info') : EMPTY)}${kv([
          ['Authorised from', muscat(a.authorisation_from)],
          ['Authorised to', muscat(a.authorisation_to)],
          ['Revision authorised', revRef(ctx, a.revision_id)],
          ...(a.authorisation_kind === 'extension' ? ([['Extends', authRef(ctx, a.supersedes_authorisation_id)]] as Row[]) : []),
        ])}${signature(a.attestation, { role: 'RS', what: a.authorisation_kind === 'extension' ? 'Extension approved' : 'Permit authorised' })}${omApprovalBlock(ctx, a.id)}</div>`,
    ),
    extensionRequestsBlock(ctx),
  ])
}

const EXTENSION_DECISION_WHAT: Record<string, string> = {
  approved: 'Extension approved',
  refused: 'Extension refused',
  withdrawn: 'Extension request withdrawn',
}

/** Every extension request with its decision and signatures (0020); a note when there are none. */
function extensionRequestsBlock(ctx: Ctx): Html {
  const list = sorted(arr(ctx.b.extension_requests), (x, y) => cmpTime(x.requested?.server_signed_at, y.requested?.server_signed_at) || cmpText(x.id, y.id))
  const s = extensionSummary(ctx.b)
  const allowance = s
    ? s.allowanceDays === null
      ? 'Extension allowance: not usable in these rules, so no extension can be approved.'
      : `Extension allowance: at most ${s.allowanceDays} days in total after the original authorised end (${muscat(s.originalTo)}), asked for by the PA or PH before the authorised end and approved by the RS.`
    : null
  if (list.length === 0) {
    return html`<div class="block">${h4('Extension requests')}${note(allowance ? `None. ${allowance}` : 'None.')}</div>`
  }
  return html`<div class="block">${h4('Extension requests')}${allowance ? note(allowance) : EMPTY}${joinHtml(
    list.map((r, i) => {
      const st = EXTENSION_STATUS_TEXT[r.status] ?? { label: humanize(r.status), tone: 'neutral' as const }
      const tone = st.tone === 'ok' ? 'ok' : st.tone === 'bad' ? 'bad' : st.tone === 'warn' ? 'warn' : 'neutral'
      const d = r.decision
      return html`<div class="block">${h4(`Request ${i + 1}: to ${muscat(r.requested_to)}`, tag(st.label.toUpperCase(), tone))}${kv([
        ['Authorised end when asked', muscat(r.previous_authorisation_to)],
        ['New end asked for', muscat(r.requested_to)],
        ['Reason', r.reason],
        ...(d?.reason ? ([[d.decision === 'refused' ? 'Why refused' : 'Why withdrawn', d.reason]] as Row[]) : []),
        ...(d?.notes ? ([['RS notes', d.notes]] as Row[]) : []),
      ])}${signature(r.requested, { role: r.requester_role, what: 'Extension requested' })}${
        d
          ? signature(d.attestation, { role: d.decision === 'withdrawn' ? r.requester_role : 'RS', what: EXTENSION_DECISION_WHAT[d.decision] ?? humanize(d.decision) })
          : r.status === 'pending'
            ? unsigned({ role: 'RS', what: 'Extension decision' }, 'Waiting for the Responsible Supervisor. Until approved, the permit is valid only to its current authorised end.')
            : note('Never decided: the permit was cancelled.')
      }</div>`
    }),
  )}</div>`
}

// -----------------------------------------------------------------------------
// Sections 9-12: daily cycles
// -----------------------------------------------------------------------------
const CHECK_RESULT: Record<string, string> = { confirmed: 'Confirmed', not_applicable: 'Not applicable', prearranged: 'Pre-arranged' }
/** The validation panel offers the gas check as Complete or Pre-arranged, so print the same word for it. */
const checkResultLabel = (code: string, result: string): string =>
  code === 'gas_tests' && result === 'confirmed' ? 'Complete' : CHECK_RESULT[result] ?? humanize(result)

const GAS_CONDITION: Record<string, string> = {
  complete: 'Complete',
  prearranged: 'Pre-arranged - work still cannot start without a completed acceptable test',
  not_required: 'Not required',
}

function cycleHeading(c: CycleView): Html {
  return h3(`Cycle ${c.cycle_number}`, tag(cycleStateLabel(c.cycle_state), c.cycle_state === 'accepted' ? 'ok' : c.cycle_state === 'suspension_reported' || c.cycle_state === 'suspended' ? 'bad' : 'neutral'))
}

function cycleFacts(ctx: Ctx, c: CycleView): Html {
  return kv([
    ['Requested window', windowText(c.requested_from, c.requested_to)],
    ['Requested by', personText(c.requested_by)],
    ['Created (server)', muscat(c.created_at, true)],
    ['Under', `${revRef(ctx, c.revision_id)}, ${authRef(ctx, c.authorisation_id)}`],
    ['Replaces', c.replaces_cycle_id ? cycleRef(ctx, c.replaces_cycle_id) : 'None (first cycle)'],
    ['Ended', c.ended_at ? `${muscat(c.ended_at, true)} - ${humanize(c.end_reason)}` : 'Not ended'],
  ])
}

function validationChecks(checks: ReadonlyArray<{ check_code: string; result: string; note: string | null }>): Html {
  const byCode = new Map<string, Array<{ result: string; note: string | null }>>()
  checks.forEach((c) => {
    const list = byCode.get(c.check_code) ?? []
    list.push({ result: c.result, note: c.note })
    byCode.set(c.check_code, list)
  })
  const rows: Cell[][] = AA_VALIDATION_CHECKS.map((o, i) => {
    const found = byCode.get(o.code) ?? []
    if (found.length === 0) return [String(i + 1), o.label, html`<span class="missing">Not recorded</span>`, null]
    return [String(i + 1), o.label, found.map((f) => checkResultLabel(o.code, f.result)).join('; '), found.map((f) => f.note ?? '').filter((x) => x !== '').join('; ') || null]
  })
  const known = new Set(AA_VALIDATION_CHECKS.map((o) => o.code))
  Array.from(byCode.keys())
    .filter((k) => !known.has(k))
    .sort()
    .forEach((k) => {
      const found = byCode.get(k) ?? []
      rows.push(['+', `${humanize(k)} (${k}) - not in the catalogue`, found.map((f) => CHECK_RESULT[f.result] ?? humanize(f.result)).join('; '), found.map((f) => f.note ?? '').join('; ') || null])
    })
  return grid(['#', 'Validation check', 'Result', 'Note'], rows, 'checks')
}

function sectionCycles(ctx: Ctx): Html {
  if (ctx.cycles.length === 0) return section(9, note('No daily cycle recorded.'))
  const examReqs = ctx.current ? arr(ctx.current.requirements).filter((q) => q.requirement_kind === 'worksite_examination') : []
  const parts: Html[] = []
  if (examReqs.length > 0) {
    parts.push(h3('Worksite examination requirements'))
    parts.push(grid(['#', 'Examination', 'Basis'], examReqs.map((q) => { const d = asRec(q.detail); return [String(q.sequence), examLabel(d.exam_kind), valueCell(d.basis)] as Cell[] })))
  }
  ctx.cycles.forEach((c) => {
    const blocks: Html[] = [cycleHeading(c), cycleFacts(ctx, c)]
    blocks.push(h4('Pre-issue by the Responsible Supervisor'))
    if (c.preissue) {
      blocks.push(checklist(PREISSUE_CHECKS, c.preissue.checks))
      blocks.push(kv([
        ['Frozen plan or MoC reference', c.preissue.plan_reference],
        ['Conflict and SIMOPS review reference', c.preissue.conflict_review_reference],
      ]))
      blocks.push(signature(c.preissue.attestation, { role: 'RS', what: 'Cycle pre-issued' }))
    } else {
      blocks.push(unsigned({ role: 'RS', what: 'Cycle pre-issued' }))
    }
    const v = c.validation
    blocks.push(h4(v?.attestation?.signing_role === 'AA' ? 'Validation by the Area Authority (historic)' : 'Validation by the Responsible Supervisor'))
    if (v) {
      const pre = asRec(v.prearrangement)
      const stop = asRec(v.stop_resolution)
      blocks.push(kv([
        ['Validated for', windowText(v.validation_from, v.validation_to)],
        ['Gas condition', isBlank(v.gas_condition) ? null : GAS_CONDITION[v.gas_condition] ?? humanize(v.gas_condition)],
      ]))
      blocks.push(validationChecks(arr(v.checks)))
      if (Object.keys(pre).length > 0) {
        const known = ['tester_person_id', 'planned_at', 'sample_location', 'species']
        blocks.push(h4('Pre-arranged gas test'))
        blocks.push(kv([
          ['Gas tester', personById(ctx, pre.tester_person_id)],
          ['Planned for', muscat(pre.planned_at)],
          ['Sample location', valueCell(pre.sample_location)],
          ['Gases', arr(pre.species as unknown[] | undefined).map((x) => labelWithCode(GAS_SPECIES, x)).join('; ') || null],
          ...Object.keys(pre).filter((k) => known.indexOf(k) < 0).sort().map((k) => [k, valueCell(pre[k])] as Row),
        ]))
      }
      if (Object.keys(stop).length > 0) {
        const known = ['note', 'resolving_gas_test_id']
        blocks.push(h4('How the earlier stop was resolved'))
        blocks.push(kv([
          ['Resolution note', valueCell(stop.note)],
          ['Resolving gas test', isBlank(stop.resolving_gas_test_id) ? 'None' : gasRef(ctx, stop.resolving_gas_test_id)],
          ...Object.keys(stop).filter((k) => known.indexOf(k) < 0).sort().map((k) => [k, valueCell(stop[k])] as Row),
        ]))
      }
      blocks.push(signature(v.attestation, { role: 'RS', what: 'Cycle validated' }))
    } else {
      blocks.push(unsigned({ role: 'RS', what: 'Cycle validated' }))
    }
    const exams = sorted(arr(c.examinations), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at) || cmpText(x.id, y.id))
    exams.forEach((x) => {
      blocks.push(h4(`Worksite examination: ${examLabel(x.exam_kind)}`))
      blocks.push(kv([
        ['Observed (reported)', muscat(x.reported_observed_at)],
        ['Findings', x.findings],
      ]))
      blocks.push(signature(x.attestation, { role: x.attestation?.signing_role ?? 'RS', what: `Worksite examination (${examLabel(x.exam_kind)})` }))
    })
    if (ctx.b.class_a) {
      const ca = arr(ctx.b.class_a.validation_conditions).find((x) => x.cycle_id === c.id)
      blocks.push(h4('Class A conditions'))
      if (ca) {
        blocks.push(kv([
          ['Validating RS stays in the work area', ca.rs_remains_in_area ? 'Declared' : 'Not declared'],
          ['Worksite examination at this validation', ca.daily_examination_required ? 'Required (Class A)' : 'Not required by the rules in use'],
          ['First gas test of the cycle', ca.first_gas_test_source === 'not_required'
            ? 'No gas test required'
            : `${ca.first_gas_tester?.display_name ?? 'Unknown'}${ca.first_gas_test_source === 'prearranged' ? ' (pre-arranged)' : ''}${ca.first_gas_test_delegated ? ', delegated by the validating RS' : ', the validating RS'}`],
          ...(ca.first_gas_test_delegated ? [['Delegation', ca.delegation_note] as Row] : []),
        ]))
        blocks.push(signature(ca.attestation, { role: ca.attestation?.signing_role ?? 'RS', what: 'Class A conditions' }))
      } else {
        blocks.push(unsigned({ role: 'RS', what: 'Class A conditions' }))
      }
    }
    blocks.push(h4('Acceptance by the Permit Holder'))
    if (c.acceptance) {
      blocks.push(checklist(ACCEPTANCE_DECLARATIONS, c.acceptance.declarations))
      blocks.push(kv([['Toolbox talk', ctx.talkNo.get(c.acceptance.toolbox_talk_id) ? `Toolbox talk ${ctx.talkNo.get(c.acceptance.toolbox_talk_id)} (section 11)` : `Toolbox talk ${shortId(c.acceptance.toolbox_talk_id)}`]]))
      blocks.push(signature(c.acceptance.attestation, { role: 'PH', what: 'Cycle accepted by the Permit Holder' }))
    } else {
      blocks.push(unsigned({ role: 'PH', what: 'Cycle accepted by the Permit Holder' }))
    }
    parts.push(html`<div class="cycle">${blocks}</div>`)
  })
  return section(9, parts)
}

const CLASSIFICATION_TEXT: Record<string, { label: string; tone: 'ok' | 'bad' | 'warn' }> = {
  acceptable: { label: 'ACCEPTABLE', tone: 'ok' },
  unsafe: { label: 'UNSAFE', tone: 'bad' },
  unresolved: { label: 'UNRESOLVED - NO APPROVED LIMITS', tone: 'warn' },
}

function gasTestBlock(ctx: Ctx, g: GasTestView): Html {
  const cls = CLASSIFICATION_TEXT[g.classification] ?? { label: humanize(g.classification).toUpperCase(), tone: 'warn' as const }
  const measurements = arr(g.measurements).map((m) => [
    labelWithCode(GAS_SPECIES, m.species_code),
    typeof m.measured_value === 'number' ? String(m.measured_value) : valueCell(m.measured_value),
    m.unit_code,
    yesNo(m.agt_declared_within_criteria, 'Not declared'),
    isBlank(m.comparison_result) ? null : humanize(m.comparison_result),
  ] as Cell[])
  return html`<div class="block">${h4(`Gas test ${ctx.gasNo.get(g.id) ?? shortId(g.id)} - ${optionLabel(GAS_TEST_KINDS, g.test_kind)}`, tag(cls.label, cls.tone))}${kv([
    ['Result', `${cls.label}${isBlank(g.classification_basis) ? '' : ` (basis: ${humanize(g.classification_basis)})`}`],
    ['Method reference', g.method_reference],
    ['Sample location', g.sample_location],
    ['When the readings were taken (reported by the AGT)', muscat(g.reported_observed_at, true)],
    ['Recorded (server)', muscat(g.recorded_at, true)],
    ['Instrument', g.instrument ? `${g.instrument.instrument_identifier} - ${g.instrument.model}` : null],
    ['Notes', g.notes],
  ])}${measurements.length > 0 ? grid(['Gas', 'Reading', 'Unit', 'AGT declared within criteria', 'Comparison'], measurements) : note('No readings recorded.')}${signature(g.attestation, {
    role: 'AGT',
    what: 'Gas test readings and declarations',
  })}</div>`
}

function sectionGas(ctx: Ctx): Html {
  if (ctx.cycles.length === 0) return section(10, note('No gas test recorded.'))
  return section(
    10,
    ctx.cycles.map((c) => {
      const tests = sorted(arr(c.gas_tests), (x, y) => cmpTime(x.recorded_at, y.recorded_at) || cmpText(x.id, y.id))
      return html`<div class="cycle">${h3(`Cycle ${c.cycle_number}`)}${tests.length > 0 ? tests.map((g) => gasTestBlock(ctx, g)) : note('No gas test recorded in this cycle.')}</div>`
    }),
  )
}

function talkBlock(ctx: Ctx, t: ToolboxTalkView): Html {
  const content = asRec(t.content)
  const covered = new Set(arr(content.topics_covered as unknown[] | undefined).map((x) => str(x)))
  const topicRows: Cell[][] = TBT_TOPICS.map((o, i) => [String(i + 1), o.label, covered.has(o.code) ? yesNo(true) : html`<span class="missing">Not covered</span>`])
  const known = new Set(TBT_TOPICS.map((o) => o.code))
  Array.from(covered)
    .filter((k) => !known.has(k))
    .sort()
    .forEach((k) => topicRows.push(['+', `${humanize(k)} (${k}) - not in the catalogue`, yesNo(true)]))
  const attendees = sorted(arr(t.attendees), (x, y) => cmpText(x.person?.display_name, y.person?.display_name) || cmpText(x.id, y.id))
  return html`<div class="block">${h4(`Toolbox talk ${ctx.talkNo.get(t.id) ?? shortId(t.id)} - given by the ${roleLabel(t.giver_role)}`, tag(t.complete ? 'ALL ATTENDEES SIGNED' : 'ATTENDANCE INCOMPLETE', t.complete ? 'ok' : 'warn'))}${kv([
    ['Conducted (reported)', muscat(t.reported_conducted_at)],
    ['Additional hazards and controls', valueCell(content.additional_hazards)],
    ['Dynamic risk assessment plan', valueCell(content.dra_plan)],
    ['Pause-and-check points', valueCell(content.pause_and_check_points)],
    ['All attendees signed (server)', yesNo(t.complete, 'Not recorded')],
  ])}${grid(['#', 'Topic', 'Covered'], topicRows, 'checks')}${signature(t.giver, { role: t.giver_role, what: 'Toolbox talk given' })}${h4('Attendees (TRIC)')}${
    attendees.length > 0
      ? grid(['Name', 'Organisation', 'Role in crew', 'Signature'], attendees.map((x) => [x.person?.display_name, x.person?.organisation, x.role_in_crew, x.signed ? html`<span class="yes">Signed ${muscat(x.signed.server_signed_at, true)}</span>` : html`<span class="missing">NOT SIGNED</span>`]))
      : note('No attendee recorded.')
  }${attendees.map((x) => signature(x.signed, { role: 'CREW', what: `Understood the toolbox talk and TRIC - ${x.person?.display_name ?? 'attendee'}` }, `${x.person?.display_name ?? 'This attendee'} has not signed.`))}</div>`
}

function sectionTalks(ctx: Ctx): Html {
  if (ctx.cycles.length === 0) return section(11, note('No toolbox talk recorded.'))
  return section(
    11,
    ctx.cycles.map((c) => {
      const talks = sorted(arr(c.toolbox_talks), (x, y) => cmpTime(x.giver?.server_signed_at, y.giver?.server_signed_at) || cmpText(x.id, y.id))
      return html`<div class="cycle">${h3(`Cycle ${c.cycle_number}`)}${talks.length > 0 ? talks.map((t) => talkBlock(ctx, t)) : [note('No toolbox talk recorded in this cycle.'), unsigned({ role: 'PH', what: 'Toolbox talk given' })]}</div>`
    }),
  )
}

function stopBlock(ctx: Ctx, s: SuspensionView): Html {
  const confirmations = sorted(arr(s.confirmations), (x, y) => cmpTime(x.attestation?.server_signed_at, y.attestation?.server_signed_at))
  const phDeclared = Boolean(s.ph_attestation) || confirmations.some((x) => x.confirmation_role === 'PH')
  // The RS confirms from 0016; an Area Authority confirmation before it counts the same.
  const supervisorConfirmed = confirmations.some((x) => x.confirmation_role === 'RS' || x.confirmation_role === 'AA')
  const blocks: Html[] = [
    h4(`Stop ${ctx.stopNo.get(s.id) ?? shortId(s.id)} - ${optionLabel(SUSPENSION_REASONS, s.reason_code)}`, tag('STOP', 'bad')),
    kv([
      ['PH safe-state declaration', phDeclared ? 'Recorded' : html`<span class="missing">Not recorded</span>`],
      ['RS confirmation', supervisorConfirmed ? 'Recorded' : html`<span class="missing">Not recorded</span>`],
      ['Condition reported', s.reported_condition],
      ['Observed (reported)', muscat(s.reported_observed_at, true)],
      ['Received (server)', muscat(s.server_received_at, true)],
      ['Reported by', s.reporter ? personText(s.reporter) : 'No person recorded'],
      ['Reported in the capacity of', isBlank(s.reporter_capacity) ? null : `${capacityLabel(s.reporter_capacity)} (${s.reporter_capacity})`],
      ['Raised by gas test', s.source_gas_test_id ? `${gasRef(ctx, s.source_gas_test_id)} (section 10)` : 'No'],
    ]),
  ]
  if (s.ph_attestation) blocks.push(signature(s.ph_attestation, { role: 'PH', what: 'Stop reported by the Permit Holder' }))
  confirmations.forEach((x) => {
    blocks.push(kv([
      [x.confirmation_role === 'PH' ? 'PH declared the site safe' : `${x.confirmation_role === 'AA' ? 'Area Authority (historic)' : 'RS'} confirmed the site safe`, yesNo(x.safe_state_confirmed, 'Not recorded')],
      ['Notes', x.notes],
    ]))
    blocks.push(signature(x.attestation, { role: x.confirmation_role, what: x.confirmation_role === 'PH' ? 'Safe state declared by the Permit Holder' : x.confirmation_role === 'AA' ? 'Suspension confirmed by the Area Authority (historic)' : 'Suspension confirmed by the Responsible Supervisor' }))
  })
  if (!phDeclared) blocks.push(unsigned({ role: 'PH', what: 'Safe state declared by the Permit Holder' }))
  if (!supervisorConfirmed) blocks.push(unsigned({ role: 'RS', what: 'Suspension confirmed by the Responsible Supervisor' }))
  return html`<div class="block">${blocks}</div>`
}

function sectionStops(ctx: Ctx): Html {
  const parts: Html[] = []
  const anyStops = ctx.cycles.some((c) => arr(c.suspensions).length > 0)
  if (!anyStops) parts.push(note('No stop reported.'))
  ctx.cycles.forEach((c) => {
    const stops = sorted(arr(c.suspensions), (x, y) => cmpTime(x.server_received_at, y.server_received_at) || cmpText(x.id, y.id))
    if (stops.length === 0) return
    const next = ctx.cycles.find((n) => n.replaces_cycle_id === c.id)
    const restart = next
      ? `Work restarts only through a new cycle: Cycle ${next.cycle_number} (${cycleStateLabel(next.cycle_state)}${next.validation ? `, validated ${muscat(next.validation.attestation?.server_signed_at)}` : ', not validated'}).`
      : 'No restart cycle recorded.'
    parts.push(html`<div class="cycle">${h3(`Cycle ${c.cycle_number}`)}${stops.map((s) => stopBlock(ctx, s))}${h4('Revalidation')}${note(restart)}</div>`)
  })
  parts.push(h3('Scope change reports'))
  const changes = sorted(arr(ctx.b.scope_changes), (x, y) => cmpTime(x.server_received_at, y.server_received_at) || cmpText(x.id, y.id))
  parts.push(
    changes.length > 0
      ? grid(
          ['Received (server)', 'Reported by', 'Description', 'Revision', 'Replacement permit'],
          changes.map((s) => [muscat(s.server_received_at, true), `${personText(s.reporter)} as ${roleLabel(s.reporter_role)}`, s.description, revRef(ctx, s.revision_id), s.replacement_permit_id ? mono(s.replacement_permit_id) : 'None yet']),
        )
      : note('None reported.'),
  )
  return section(12, parts)
}

// -----------------------------------------------------------------------------
// Section 13: PH cancellation and RS closeout (AA closeout on records before 0016)
// -----------------------------------------------------------------------------
function sectionClose(ctx: Ctx): Html {
  const c = ctx.b.cancellation
  const k = ctx.b.closeout
  const parts: Html[] = [h3('Cancellation by the Permit Holder')]
  if (c) {
    parts.push(kv([
      ['Reason', optionLabel(CANCELLATION_REASONS, c.reason_code)],
      ['Work complete', yesNo(c.work_complete, 'Not recorded')],
      ['Worksite condition', c.worksite_condition],
      ['Tools and equipment removed', yesNo(c.tools_equipment_removed, 'Not recorded')],
      ['Work continues on (GOV-035)', c.continuation_reference ?? (c.work_complete ? 'Not applicable (work complete)' : 'Not stated')],
      ['Isolations', ISOLATION_DISPOSITION_LABELS[c.isolation_disposition] ?? humanize(c.isolation_disposition)],
    ]))
    parts.push(checklist(PH_CANCELLATION_DECLARATIONS, c.declarations))
    parts.push(signature(c.attestation, { role: 'PH', what: 'Permit cancelled by the Permit Holder' }))
  } else {
    parts.push(unsigned({ role: 'PH', what: 'Permit cancelled by the Permit Holder' }))
  }
  const historic = k?.attestation?.signing_role === 'AA'
  parts.push(h3(historic ? 'Closeout by the Area Authority (historic)' : 'Closeout by the Responsible Supervisor'))
  if (k) {
    parts.push(kv([
      ['Site left safe', yesNo(k.site_safe_confirmed, 'Not recorded')],
      ['Work status confirmed', humanize(k.work_status_confirmed)],
      ['Inspection findings', k.inspection_findings],
      ['Operation check', k.operation_check],
      ['Isolations', `${ISOLATION_DISPOSITION_LABELS[k.isolation_disposition] ?? humanize(k.isolation_disposition)}${isBlank(k.isolation_disposition_note) ? '' : ` - ${str(k.isolation_disposition_note)}`}`],
    ]))
    parts.push(checklist(AA_CLOSEOUT_CHECKS, k.checks))
    parts.push(signature(k.attestation, { role: 'RS', what: historic ? 'Permit closed out by the Area Authority (historic)' : 'Permit closed out by the Responsible Supervisor' }))
  } else {
    parts.push(unsigned({ role: 'RS', what: 'Permit closed out by the Responsible Supervisor' }))
  }
  return section(13, parts)
}

// -----------------------------------------------------------------------------
// Section 14: record control
// -----------------------------------------------------------------------------
function sectionRecord(ctx: Ctx): Html {
  const b = ctx.b
  const parts: Html[] = [h3('Signature register')]
  parts.push(
    ctx.attestations.length > 0
      ? grid(
          ['#', 'Signer', 'Role', 'Capacity', 'Server time', 'Content digest', 'Credential'],
          ctx.attestations.map((a, i) => [
            String(i + 1),
            personText(a.signer),
            roleLabel(a.signing_role),
            capacityLabel(a.declared_capacity),
            muscat(a.server_signed_at, true),
            mono(a.content_digest),
            a.credential ? `${a.credential.role_code} ${humanize(a.credential.credential_kind)}${a.credential.expires_on ? `, expires ${dateOnly(a.credential.expires_on)}` : ''}` : 'None',
          ]),
          'register',
        )
      : note('No signatures recorded.'),
  )
  parts.push(h3('Event log'))
  const events = sorted(arr(b.events), (x, y) => cmpNum(x.sequence, y.sequence))
  parts.push(
    events.length > 0
      ? grid(
          ['#', 'Server time', 'Event', 'By', 'Applies to', 'Version', 'Details'],
          events.map((e) => [
            String(e.sequence),
            muscat(e.server_recorded_at, true),
            EVENT_KIND_LABELS[e.event_kind] ?? humanize(e.event_kind),
            e.actor ? personText(e.actor) : humanize(e.actor_kind),
            [e.revision_id ? revRef(ctx, e.revision_id) : '', e.cycle_id ? cycleRef(ctx, e.cycle_id) : ''].filter((x) => x !== '').join(', ') || null,
            e.new_row_version !== null && e.new_row_version !== undefined ? `v${e.prior_row_version ?? '-'} -> v${e.new_row_version}` : null,
            details(e.details, (v) => idLabel(ctx, v)),
          ]),
          'events',
        )
      : note('No events recorded.'),
  )
  parts.push(h3('Archive'))
  if (ctx.bundleVersion) {
    parts.push(note(`This document renders the archive bundle (${ctx.bundleVersion}). Where the archived files are stored is recorded with the archive job, outside the bundle.`))
  } else {
    const a = ctx.live?.archive
    if (!a) {
      parts.push(note('No archive job yet. It is created when the Responsible Supervisor closes out the permit.'))
    } else {
      const j = a.job
      parts.push(kv([
        ['Archive job status', humanize(j?.status)],
        ['Attempts', valueCell(j?.attempt_count)],
        ['Last error', valueCell(j?.last_error)],
        ['Bundle digest', mono(j?.bundle_digest)],
        ['Retention', j?.retention_status === 'unresolved' ? 'Unresolved - no approved retention rule yet (DEC-GOV-007)' : humanize(j?.retention_status)],
        ['Requested (server)', muscat(j?.requested_at, true)],
        ['Completed (server)', j?.completed_at ? muscat(j.completed_at, true) : 'Not completed'],
      ]))
      const artifacts = arr(a.artifacts)
      parts.push(
        artifacts.length > 0
          ? grid(['File', 'Bucket', 'Path', 'SHA-256', 'Bytes', 'Template'], artifacts.map((x) => [humanize(x.artifact_kind), x.storage_bucket, mono(x.storage_path), mono(x.sha256), String(x.byte_count), x.render_template_version]))
          : note('No archived files recorded yet.'),
      )
    }
  }
  return section(14, parts)
}

function appendices(ctx: Ctx): Html {
  const out: Html[] = []
  const superseded = sorted(
    ctx.revisions.filter((r) => r.id !== ctx.current?.id),
    (x, y) => cmpNum(y.revision_number, x.revision_number),
  )
  superseded.forEach((r) => {
    const src: AppSource = { kind: 'revision', revision: r, fields: asRec(r.content), main: false }
    out.push(
      html`<section class="appendix"><h2 class="app-title">Appendix: superseded revision ${r.revision_number} - not the current scope</h2>${APP_PARTS.map(
        (render, i) => html`${h3(`${i + 1}. ${PERMIT_SECTIONS[i].title}`)}${render(ctx, src)}`,
      )}</section>`,
    )
  })
  const draft = ctx.live?.draft
  if (draft && ctx.current && (ctx.state === 'draft' || ctx.state === 'returned_for_revision')) {
    const src: AppSource = { kind: 'draft', revision: null, fields: asRec(draft.fields), main: false }
    out.push(
      html`<section class="appendix"><h2 class="app-title">Appendix: unsubmitted draft version ${draft.draft_version} - not signed, not authorised</h2>${APP_PARTS.map(
        (render, i) => html`${h3(`${i + 1}. ${PERMIT_SECTIONS[i].title}`)}${render(ctx, src)}`,
      )}</section>`,
    )
  }
  return joinHtml(out)
}

function recordControl(ctx: Ctx): Html {
  const b = ctx.b
  const p = b.permit
  const policy = b.policy
  const events = arr(b.events)
  const last = sorted(events, (x, y) => cmpNum(x.sequence, y.sequence))[events.length - 1]
  const rows: Row[] = [
    ['Permit', `${p?.permit_number || '-'} (id ${p?.id || '-'})`],
    ['Current revision', ctx.current ? `Revision ${ctx.current.revision_number} of ${ctx.revisions.length}, sealed ${muscat(ctx.current.sealed_at, true)}` : 'None sealed'],
    ['Scope digest', mono(ctx.current?.scope_digest)],
    ['Rules version', policy ? `${policy.version_label || '-'} - ${policyStatusLabel(policy.status)} (${policy.profile_code || '-'}, id ${policy.version_id || '-'})` : 'Not stated'],
    ['Bundle', ctx.bundleVersion ? `${ctx.bundleVersion} (canonical archive bundle)` : 'Live read from the server (not an archive bundle)'],
  ]
  if (ctx.live) {
    const rowVersion = p && 'row_version' in p ? p.row_version : null
    rows.push(['Permit version', isBlank(rowVersion) ? null : `v${str(rowVersion)}`])
    rows.push(['Data read from the server', muscat(ctx.live.generated_at, true)])
  }
  rows.push(['Signatures and events', `${ctx.attestations.length} signatures, ${events.length} events${last ? ` (last #${last.sequence}, ${EVENT_KIND_LABELS[last.event_kind] ?? humanize(last.event_kind)}, ${muscat(last.server_recorded_at, true)})` : ''}`])
  rows.push(['Template version', RENDER_TEMPLATE_VERSION])
  rows.push(['Generated', ctx.opts.generatedAt ? `${muscat(ctx.opts.generatedAt, true)}${ctx.opts.mode === 'archive' ? ' (time of the last recorded event)' : ''}` : 'Time not stated'])
  rows.push(['Mode', ctx.opts.mode === 'archive' ? 'Archive rendering' : 'Printed copy'])
  rows.push(['Synthetic', ctx.synthetic ? 'Yes - fictitious people, sites and qualifications' : 'No'])
  if (ctx.staging) rows.push(['Environment', 'Staging - a test record for verification, not a permit to work'])
  return html`<section class="record-control" id="record-control"><h2 class="app-title">Record control</h2>${kv(rows)}</section>`
}

// -----------------------------------------------------------------------------
// Styles: one light theme, A4 portrait in print, readable on a phone screen.
// -----------------------------------------------------------------------------
const CSS = `
@page { size: A4 portrait; margin: 12mm 11mm 13mm 11mm; }
@page { @bottom-right { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 7.5pt; color: #545a7a; } }
:root { color-scheme: light; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
html { background: #ffffff; color: #1b1f3b; font-family: "Segoe UI", "IBM Plex Sans", Arial, Helvetica, sans-serif; font-size: 9.5pt; line-height: 1.35; -webkit-text-size-adjust: 100%; }
body { margin: 0; }
.doc { position: relative; border-left: 2.5mm solid #8a8fab; padding-left: 4mm; }
.doc.cls-b { border-left-color: #1d4ed8; }
.doc.cls-a { border-left-color: #b91c1c; }
table.part { width: 100%; border-collapse: collapse; table-layout: fixed; }
table.part > thead > tr > td, table.part > tfoot > tr > td, table.part > tbody > tr > td { padding: 0; vertical-align: top; }
.run-head { display: flex; flex-wrap: wrap; gap: 1mm 4mm; align-items: baseline; border-bottom: 1.2pt solid #545a7a; padding: 0 0 1.2mm; margin: 0 0 3mm; font-size: 8pt; }
.run-head.cls-b { border-bottom-color: #1d4ed8; }
.run-head.cls-a { border-bottom-color: #b91c1c; }
.rh-no { font-weight: 700; font-size: 9.5pt; }
.rh-class { font-weight: 700; }
.rh-state { font-weight: 700; border: 0.8pt solid #1b1f3b; padding: 0 1.5mm; }
.rh-syn { font-weight: 700; color: #8a4b00; }
.rh-title { color: #545a7a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 70mm; }
.run-foot { border-top: 0.6pt solid #b9bdd1; margin-top: 3mm; padding-top: 1mm; font-size: 7pt; color: #545a7a; }
.watermark { position: fixed; left: 50%; top: 50%; width: 190mm; transform: translate(-50%, -50%) rotate(-32deg); text-align: center; font-family: "Arial Narrow", "IBM Plex Sans Condensed", Arial, sans-serif; font-weight: 700; font-size: 32pt; line-height: 1.25; letter-spacing: 0.04em; color: rgba(138, 75, 0, 0.12); z-index: 10; pointer-events: none; }
.wm-line { display: block; white-space: nowrap; }
.watermark.with-draft { top: 70%; }
.draft-wm { position: fixed; left: 50%; top: 38%; transform: translate(-50%, -50%) rotate(-32deg); font-family: "Arial Narrow", "IBM Plex Sans Condensed", Arial, sans-serif; font-weight: 800; font-size: 120pt; line-height: 1; letter-spacing: 0.12em; color: rgba(156, 0, 6, 0.13); border: 6pt solid rgba(156, 0, 6, 0.13); padding: 2mm 8mm; border-radius: 4mm; z-index: 10; pointer-events: none; white-space: nowrap; }
.band { padding: 2mm 3mm; color: #ffffff; background: #545a7a; font-weight: 700; letter-spacing: 0.08em; font-size: 10pt; border-radius: 1mm; }
.band.cls-b { background: #1d4ed8; }
.band.cls-a { background: #b91c1c; }
.head-grid { display: flex; gap: 5mm; align-items: flex-start; margin-top: 3mm; }
.head-main { flex: 1 1 auto; min-width: 0; }
.org { margin: 0; font-weight: 600; color: #545a7a; letter-spacing: 0.04em; text-transform: uppercase; font-size: 8.5pt; display: flex; align-items: center; gap: 3mm; }
.logo { width: 15mm; height: auto; flex: none; }
h1.pno { font-size: 20pt; margin: 0.5mm 0 0; letter-spacing: 0.02em; }
.ptitle { font-size: 12pt; font-weight: 600; margin: 0.5mm 0 2mm; }
.qr-box { flex: 0 0 36mm; text-align: center; border: 0.8pt solid #b9bdd1; border-radius: 1.5mm; padding: 2mm; }
img.qr { width: 28mm; height: 28mm; display: block; margin: 0 auto 1mm; }
.qr-note { font-size: 7.5pt; font-weight: 700; margin: 0; }
.qr-url { font-size: 6.5pt; color: #545a7a; overflow-wrap: anywhere; word-break: break-word; margin: 0.5mm 0 0; }
.stamp { border: 1.5pt solid #545a7a; border-radius: 1.5mm; padding: 2mm 3mm; margin: 3mm 0; break-inside: avoid; page-break-inside: avoid; }
.stamp p { margin: 0.5mm 0; }
.stamp-title { font-weight: 700; font-size: 11pt; }
.tone-info { border-color: #1f6fb2; background: #e1eefa; }
.tone-warn { border-color: #a15c07; background: #fdf1dc; }
.tone-bad { border-color: #c62828; background: #fde4e4; }
.syn-note { border: 1pt dashed #8a4b00; background: #fff4dc; color: #8a4b00; font-weight: 700; padding: 1.5mm 3mm; margin: 2mm 0; border-radius: 1mm; }
.tz-note { font-size: 8pt; color: #545a7a; margin: 1mm 0 0; }
h2.sec-title, h2.app-title { display: flex; flex-wrap: wrap; align-items: baseline; gap: 1mm 2.5mm; font-size: 12.5pt; margin: 6mm 0 2.5mm; padding-bottom: 1mm; border-bottom: 1pt solid #b9bdd1; break-after: avoid; page-break-after: avoid; }
.sec-no { display: inline-block; min-width: 7mm; text-align: center; background: #1b1f3b; color: #ffffff; border-radius: 1mm; font-size: 10pt; padding: 0.3mm 1mm; }
.sec-signer { margin-left: auto; font-size: 8pt; font-weight: 400; color: #545a7a; }
h3.sub { font-size: 10.5pt; margin: 4mm 0 1.5mm; break-after: avoid; page-break-after: avoid; display: flex; flex-wrap: wrap; gap: 2mm; align-items: baseline; }
h4.sub2 { font-size: 9.5pt; margin: 3mm 0 1mm; break-after: avoid; page-break-after: avoid; display: flex; flex-wrap: wrap; gap: 2mm; align-items: baseline; }
.note { color: #545a7a; margin: 1mm 0 2mm; }
.block { margin: 2mm 0 3mm; }
.cycle { border-top: 0.8pt dashed #b9bdd1; margin-top: 3mm; }
table.kv, table.grid { width: 100%; border-collapse: collapse; margin: 1mm 0 2.5mm; }
table.kv th { text-align: left; vertical-align: top; width: 34%; color: #545a7a; font-weight: 600; padding: 0.8mm 2mm 0.8mm 0; border-bottom: 0.5pt solid #d9dce8; }
table.kv td { vertical-align: top; padding: 0.8mm 0; border-bottom: 0.5pt solid #d9dce8; overflow-wrap: break-word; }
table.grid { font-size: 8.5pt; }
table.grid th { text-align: left; vertical-align: bottom; background: #eef0f6; font-weight: 600; padding: 0.8mm 1.2mm; border: 0.5pt solid #d9dce8; }
table.grid td { vertical-align: top; padding: 0.8mm 1.2mm; border: 0.5pt solid #d9dce8; overflow-wrap: break-word; }
table.grid tr, table.kv tr { break-inside: avoid; page-break-inside: avoid; }
.idname { color: #545a7a; }
table.events td, table.register td { font-size: 7.5pt; }
.txt { white-space: pre-wrap; }
.mono { font-family: Consolas, "IBM Plex Mono", "Courier New", monospace; font-size: 7.5pt; overflow-wrap: anywhere; word-break: break-all; }
.small { font-size: 7pt; color: #545a7a; }
.muted { color: #8a8fab; }
.yes { font-weight: 600; color: #1a7f37; }
.no { font-weight: 600; color: #a15c07; }
.missing { font-weight: 600; font-style: italic; color: #c62828; }
.det { display: inline-block; }
.tag { display: inline-block; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.04em; border: 0.8pt solid #545a7a; border-radius: 1mm; padding: 0 1.5mm; color: #1b1f3b; background: #ffffff; }
.tag-ok { border-color: #1a7f37; color: #1a7f37; background: #e3f4e8; }
.tag-warn { border-color: #a15c07; color: #a15c07; background: #fdf1dc; }
.tag-bad { border-color: #c62828; color: #c62828; background: #fde4e4; }
.tag-info { border-color: #1f6fb2; color: #1f6fb2; background: #e1eefa; }
.tag-syn { border-color: #8a4b00; color: #8a4b00; background: #fff4dc; }
.sig { border: 1pt solid #8a8fab; border-left: 3.5pt solid #1a7f37; border-radius: 1.5mm; padding: 1.5mm 3mm; margin: 2mm 0; background: #fbfcff; break-inside: avoid; page-break-inside: avoid; }
.sig p { margin: 0.5mm 0; }
.sig-head { display: flex; flex-wrap: wrap; gap: 1mm 2mm; align-items: baseline; }
.sig-mark { display: inline-block; font-weight: 700; font-size: 8pt; letter-spacing: 0.06em; padding: 0.2mm 1.6mm; border-radius: 1mm; }
.mark-signed { background: #e3f4e8; color: #1a7f37; border: 0.8pt solid #1a7f37; }
.sig-for { font-weight: 600; }
.sig-name { font-size: 10.5pt; font-weight: 700; }
.sig-org { font-weight: 400; color: #545a7a; font-size: 9pt; }
dl.sig-kv { display: grid; grid-template-columns: max-content minmax(0, 1fr) max-content minmax(0, 1fr); gap: 0.4mm 2.5mm; margin: 0.8mm 0 0; font-size: 8pt; }
dl.sig-kv dt { color: #545a7a; font-weight: 600; }
dl.sig-kv dd { margin: 0; min-width: 0; overflow-wrap: break-word; }
dl.sig-kv dd.wide { grid-column: 2 / -1; }
.sig-unsigned { border: 1.2pt dashed #c62828; border-left-width: 1.2pt; background: repeating-linear-gradient(-45deg, #ffffff 0, #ffffff 6px, #fbeeee 6px, #fbeeee 12px); }
.mark-unsigned { background: #ffffff; color: #c62828; border: 0.8pt dashed #c62828; }
.sig-empty { font-style: italic; color: #545a7a; }
.appendix { margin-top: 8mm; }
.record-control { margin-top: 8mm; border: 1pt solid #1b1f3b; border-radius: 1.5mm; padding: 1mm 3mm 2mm; break-inside: avoid; page-break-inside: avoid; }
.record-control h2.app-title { margin-top: 1mm; }
@media screen {
  html { background: #e9ebf3; font-size: 14px; }
  .doc { max-width: 210mm; margin: 16px auto; background: #ffffff; padding: 12mm 10mm 12mm 12mm; box-shadow: 0 1px 4px rgba(27, 31, 59, 0.18); }
  table.part > thead, table.part > tfoot { display: none; }
  table.part + table.part { border-top: 1px dashed #b9bdd1; }
  table.part + table.part > tbody > tr > td { padding-top: 6mm; }
  .tw { overflow-x: auto; }
}
@media screen and (max-width: 640px) {
  .doc { margin: 0; padding: 12px 10px 16px 12px; box-shadow: none; }
  .head-grid { flex-wrap: wrap; }
  .qr-box { flex-basis: 100%; }
  table.kv th { width: 40%; }
  table.kv td, table.grid td, dl.sig-kv dd, .ptitle, h1.pno { overflow-wrap: anywhere; }
  dl.sig-kv { grid-template-columns: max-content minmax(0, 1fr); }
  dl.sig-kv dd.wide { grid-column: 2; }
  .watermark { width: 96vw; font-size: 19px; }
}
@media print {
  html, body { background: #ffffff; }
  table.part + table.part { break-before: page; page-break-before: always; }
  table.part > thead { display: table-header-group; }
  table.part > tfoot { display: table-footer-group; }
  .sig, .stamp, .record-control { break-inside: avoid; page-break-inside: avoid; }
}
`

// -----------------------------------------------------------------------------
// Entry point
// -----------------------------------------------------------------------------
export function renderPermitHtml(bundle: ArchiveBundle | PermitBundle, options: RenderOptions = { mode: 'print' }): string {
  const opts: RenderOptions = { ...options, mode: options.mode === 'archive' ? 'archive' : 'print' }
  const ctx = buildContext(bundle, opts)
  const p = bundle.permit
  const sections = applicationSections(ctx)
  const mark = watermarkText(ctx)
  const draft = isDraftCopy(ctx)
  const parts = [
    part(ctx, [headerBlock(ctx), ...sections]),
    part(ctx, [sectionReview(ctx), sectionAuthorisation(ctx)]),
    part(ctx, [sectionCycles(ctx), sectionGas(ctx), sectionTalks(ctx), sectionStops(ctx)]),
    part(ctx, [sectionClose(ctx)]),
    part(ctx, [sectionRecord(ctx), appendices(ctx), recordControl(ctx)]),
  ]
  const title = `${p?.permit_number || 'Permit'} - ${p?.title || 'Untitled'}${ctx.synthetic ? ' (synthetic demonstration)' : ctx.staging ? ' (staging test record)' : ''}`
  const doc = html`<!DOCTYPE html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="generator" content="BadrEOR ePTW ${RENDER_TEMPLATE_VERSION} (${opts.mode})">
<title>${title}</title>
<style>${new Html(CSS)}</style>
</head>
<body class="mode-${opts.mode}">
${draft ? draftWatermark() : EMPTY}
${mark ? watermark(mark, draft) : EMPTY}
<main class="doc ${classKey(p?.permit_class)}">
${parts}
</main>
</body>
</html>
`
  return doc.value
}
